/* =====================================================================
 * pptxPatcher.ts — 편집된 슬라이드만 원본 OOXML에 패치하여 새 PPTX 생성
 *
 * 전략:
 * 1. JSZip으로 원본 PPTX 열기
 * 2. dirty 슬라이드만 순회하여 해당 slideN.xml의 텍스트/서식 패치
 * 3. 나머지 파트(media, theme, rels 등)는 원본 그대로 유지
 * 4. JSZip.generateAsync로 새 PPTX Blob 생성
 * ===================================================================== */

import type {
  PptxPresentation,
  PptxSlide,
  PptxShape,
  PptxTextBody,
  PptxParagraph,
  PptxRun,
  PptxFill,
  PptxOutline,
  PptxLineEnd,
  PptxSlideArchiveSnapshot,
} from './pptxParser';
import { pxToEmu } from './pptxParser';
import { patchPaintedBody, patchPaintedParagraph, patchPaintedRun, patchPaintedShape } from './pptxFormatPainterPatch';
import { patchPptxImageProperties } from './pptxImagePatch';

/* ------------------------------------------------------------------ */
/* XML 네임스페이스                                                      */
/* ------------------------------------------------------------------ */

const P_NS = 'http://schemas.openxmlformats.org/presentationml/2006/main';
const A_NS = 'http://schemas.openxmlformats.org/drawingml/2006/main';

/* ------------------------------------------------------------------ */
/* 메인 패치 함수                                                       */
/* ------------------------------------------------------------------ */

export async function patchPptx(
  pres: PptxPresentation,
  dirtySlideIds: Set<string>,
): Promise<Blob> {
  if (dirtySlideIds.size === 0 && !pres.slideListDirty) {
    return await pres.zip.generateAsync({ type: 'blob' });
  }

  // 슬라이드 목록 변경(추가/복제/삭제/이동) — 파트 생성/삭제 + sldIdLst/rels/
  // [Content_Types] 재조정. 신규/복제 파트를 rawParts 에 등록해 두면 아래
  // per-slide 루프가 그 위에 모델 편집분을 그대로 패치한다.
  if (pres.slideListDirty) {
    await reconcilePresentation(pres, dirtySlideIds);
  }

  // 편집기가 삽입한 새 이미지 — media 파트/슬라이드 rel/[Content_Types] Default
  // 등록(reconcile 이후 = 새 슬라이드 partPath 발급 이후). 멱등.
  if (pres.newImageMedia?.size) {
    await registerNewImages(pres, dirtySlideIds);
  }

  for (const slide of pres.slides) {
    if (!dirtySlideIds.has(slide.id)) continue;

    const originalXml = pres.rawParts.get(slide.partPath);
    if (!originalXml) continue;

    const xmlDoc = new DOMParser().parseFromString(originalXml, 'application/xml');
    const cSld = xmlDoc.getElementsByTagNameNS(P_NS, 'cSld')[0];
    if (!cSld) continue;

    const spTree = cSld.getElementsByTagNameNS(P_NS, 'spTree')[0];
    if (!spTree) continue;

    // 구조편집(추가/삭제/복제/z-order) 슬라이드는 모델을 SoT 로 spTree 를
    // 재구성한다. 무구조편집 슬라이드는 종전 in-place 패치(미매칭 요소 보존).
    if (slide.structuralDirty) {
      // 전역 idMap: duplicatedFrom 클론의 컨테이너 스코프 미스 폴백 — 그룹화(원본이
      // 최상위, 클론은 새 grpSp 안)·그룹 해제(원본이 grpSp 안, 클론은 최상위)처럼
      // 원본 XML 이 다른 컨테이너에 있을 때 충실도 클론을 가능하게 한다.
      reconcileShapeTree(
        xmlDoc, spTree, slide.shapes,
        new Set(slide.deletedCNvIds ?? []),
        buildGlobalIdMap(spTree),
      );
    } else {
      patchShapeTree(xmlDoc, spTree, slide.shapes);
    }

    // 슬라이드 숨김(건너뛰기) — p:sld/@show 토글(편집 시에만 되쓰기).
    if (slide.hiddenDirty) {
      if (slide.hidden) xmlDoc.documentElement.setAttribute('show', '0');
      else xmlDoc.documentElement.removeAttribute('show');
    }
    // 배경 변경 — p:bg(cSld 첫 자식) upsert. 편집 시에만 되쓰기.
    if (slide.backgroundDirty) {
      await patchSlideBackground(xmlDoc, cSld, slide.background, pres, slide.partPath);
    }
    await pruneUnusedDeckBackgroundRelations(pres, slide.partPath, xmlDoc);
    // 레이아웃 적용 — 슬라이드 _rels 의 slideLayout rel Target 교체.
    if (slide.layoutDirty && slide.layoutRef) {
      await patchSlideLayoutRel(pres, slide.partPath, slide.layoutRef);
    }

    const serializer = new XMLSerializer();
    let newXml = serializer.serializeToString(xmlDoc);
    // XMLSerializer 는 <?xml … standalone="yes"?> 선언을 떨군다 — 원본 선언을
    // 보존해 파서/soffice 의 standalone 처리 차이를 피한다(편집 슬라이드는
    // canonical-XML 동등만 보장, 바이트동일은 아님).
    const declMatch = originalXml.match(/^<\?xml[^?]*\?>\s*/);
    if (declMatch && !newXml.startsWith('<?xml')) {
      newXml = declMatch[0] + newXml;
    }
    // ★ createFolders:false — JSZip 기본값(true)은 'ppt/','ppt/slides/' 같은 ZIP 폴더
    //   엔트리를 자동 생성한다. OPC(ECMA-376 Part2) 패키지는 폴더 엔트리를 갖지 않아야
    //   하고(원본에도 없음), PowerPoint 는 이를 만나면 "복구" 다이얼로그로 뜰 수 있다.
    //   폴더 엔트리 없이 파일 엔트리만 갱신 → 원본과 동일한 패키지 구조 유지.
    pres.zip.file(slide.partPath, newXml, { createFolders: false });
  }

  return await pres.zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
}

/* ------------------------------------------------------------------ */
/* 새 이미지 등록 — media 파트 + 슬라이드 rel + [Content_Types] Default   */
/* ------------------------------------------------------------------ */

const IMAGE_CONTENT_TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
};

/** dataURL → 바이트(Uint8Array). data: 접두 없는 순수 base64 도 허용. */
function dataUrlToBytes(dataUrl: string): Uint8Array {
  const b64 = dataUrl.includes(',') ? dataUrl.slice(dataUrl.indexOf(',') + 1) : dataUrl;
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/**
 * 편집기가 삽입한 이미지(synthetic pic, imageRId ∈ pres.newImageMedia)를 저장
 * 가능한 형태로 등록한다: ① ppt/media/imageN.ext 파트 ② 슬라이드 _rels 의
 * image Relationship(모델 imageRId 그대로 — 렌더 media 맵 키와 일치)
 * ③ [Content_Types] Default(확장자). 재저장 멱등: rels 에 이미 있으면 스킵.
 */
async function registerNewImages(pres: PptxPresentation, dirtySlideIds: Set<string>): Promise<void> {
  const zip = pres.zip;
  let maxImageNum = 0;
  zip.forEach((p) => {
    const m = /^ppt\/media\/image(\d+)\./.exec(p);
    if (m) maxImageNum = Math.max(maxImageNum, Number(m[1]));
  });

  const ctPath = '[Content_Types].xml';
  const ctXml = await readZipText(zip, ctPath);
  if (!ctXml) return;
  const ctDoc = new DOMParser().parseFromString(ctXml, 'application/xml');
  let ctChanged = false;
  const ensureDefault = (ext: string, type: string) => {
    const exists = Array.from(ctDoc.getElementsByTagNameNS(CT_NS, 'Default'))
      .some((d) => (d.getAttribute('Extension') || '').toLowerCase() === ext);
    if (exists) return;
    const el = ctDoc.createElementNS(CT_NS, 'Default');
    el.setAttribute('Extension', ext);
    el.setAttribute('ContentType', type);
    ctDoc.documentElement.appendChild(el);
    ctChanged = true;
  };

  // 그룹 자식으로 들어간 세션 삽입 이미지도 수집해야 rel/미디어가 등록된다
  // — 최상위만 스캔하면 그룹화된 이미지가 dangling r:embed 로 남는다(리뷰 확정).
  const collectNewImages = (shapes: PptxShape[], out: PptxShape[]) => {
    for (const s of shapes) {
      if ((s.synthetic || s.imageSourceDirty) && s.type === 'image' && s.imageRId && pres.newImageMedia!.has(s.imageRId)) out.push(s);
      if (s.children) collectNewImages(s.children, out);
    }
  };

  for (const slide of pres.slides) {
    if (!dirtySlideIds.has(slide.id) || !slide.partPath) continue;
    const newImages: PptxShape[] = [];
    collectNewImages(slide.shapes, newImages);
    if (newImages.length === 0) continue;

    const relsPath = relsPathOf(slide.partPath);
    const relsXml = await readZipText(zip, relsPath);
    if (!relsXml) continue; // 슬라이드 rels 는 항상 존재(레이아웃 rel)
    const relsDoc = new DOMParser().parseFromString(relsXml, 'application/xml');
    let relsChanged = false;

    for (const shape of newImages) {
      const rId = shape.imageRId!;
      if (relEls(relsDoc, (e) => e.getAttribute('Id') === rId).length > 0) continue; // 멱등
      const media = pres.newImageMedia!.get(rId)!;
      const ext = (IMAGE_CONTENT_TYPES[media.ext.toLowerCase()] ? media.ext.toLowerCase() : 'png');
      const mediaPart = `ppt/media/image${++maxImageNum}.${ext}`;
      zip.file(mediaPart, dataUrlToBytes(media.dataUrl), { createFolders: false });
      ensureDefault(ext, IMAGE_CONTENT_TYPES[ext]);
      const rel = relsDoc.createElementNS(REL_NS, 'Relationship');
      rel.setAttribute('Id', rId);
      rel.setAttribute('Type', `${R_NS}/image`);
      rel.setAttribute('Target', `../media/image${maxImageNum}.${ext}`);
      relsDoc.documentElement.appendChild(rel);
      relsChanged = true;
    }
    if (relsChanged) writeXmlPart(pres, relsPath, relsDoc, relsXml);
  }
  if (ctChanged) writeXmlPart(pres, ctPath, ctDoc, ctXml);
}

/* ------------------------------------------------------------------ */
/* 프레젠테이션(슬라이드 목록) 재조정 — 추가/복제/삭제/이동                 */
/* ------------------------------------------------------------------ */

const REL_NS = 'http://schemas.openxmlformats.org/package/2006/relationships';
const CT_NS = 'http://schemas.openxmlformats.org/package/2006/content-types';
const R_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const RELTYPE_SLIDE = `${R_NS}/slide`;
const RELTYPE_NOTES = `${R_NS}/notesSlide`;
const RELTYPE_LAYOUT = `${R_NS}/slideLayout`;
const CT_SLIDE = 'application/vnd.openxmlformats-officedocument.presentationml.slide+xml';
const CT_NOTES = 'application/vnd.openxmlformats-officedocument.presentationml.notesSlide+xml';

async function readZipText(zip: PptxPresentation['zip'], path: string): Promise<string | null> {
  const f = zip.file(path);
  return f ? await f.async('text') : null;
}

/** XMLSerializer 가 떨구는 <?xml …?> 선언을 원본에서 복원해 zip 에 기록. */
function writeXmlPart(pres: PptxPresentation, path: string, doc: Document, originalXml: string): void {
  let xml = new XMLSerializer().serializeToString(doc);
  const decl = originalXml.match(/^<\?xml[^?]*\?>\s*/);
  if (decl && !xml.startsWith('<?xml')) xml = decl[0] + xml;
  pres.zip.file(path, xml, { createFolders: false });
}

/** 'ppt/slides/slide3.xml' ↔ '../slides/slide3.xml' (presentation.xml 기준 상대경로). */
const slidePartToPresRel = (partPath: string): string => partPath.replace(/^ppt\//, '');

/** 파트의 _rels 경로('ppt/slides/slide3.xml' → 'ppt/slides/_rels/slide3.xml.rels'). */
const relsPathOf = (partPath: string): string => {
  const i = partPath.lastIndexOf('/');
  return `${partPath.slice(0, i)}/_rels/${partPath.slice(i + 1)}.rels`;
};

/** 슬라이드 파트 기준 상대 target → 절대 partPath ('../slideLayouts/x.xml' → 'ppt/slideLayouts/x.xml'). */
const slideRelTargetToPart = (target: string): string =>
  target.startsWith('/') ? target.slice(1) : `ppt/${target.replace(/^\.\.\//, '')}`;

/** 새 빈 슬라이드 파트 XML(placeholder 는 모델 도형으로 reconcileShapeTree 가 채운다). */
function blankSlideXml(): string {
  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\r\n'
    + `<p:sld xmlns:a="${A_NS}" xmlns:r="${R_NS}" xmlns:p="${P_NS}">`
    + '<p:cSld><p:spTree><p:nvGrpSpPr><p:cNvPr id="1" name=""/><p:cNvGrpSpPr/><p:nvPr/></p:nvGrpSpPr>'
    + '<p:grpSpPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="0" cy="0"/>'
    + '<a:chOff x="0" y="0"/><a:chExt cx="0" cy="0"/></a:xfrm></p:grpSpPr></p:spTree></p:cSld>'
    + '<p:clrMapOvr><a:masterClrMapping/></p:clrMapOvr></p:sld>';
}

/** 새 빈 슬라이드의 _rels(레이아웃 참조 1개). */
function blankSlideRelsXml(layoutPartPath: string): string {
  const target = `../${layoutPartPath.replace(/^ppt\//, '')}`;
  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\r\n'
    + `<Relationships xmlns="${REL_NS}">`
    + `<Relationship Id="rId1" Type="${RELTYPE_LAYOUT}" Target="${target}"/>`
    + '</Relationships>';
}

/** rels 문서에서 조건 매칭 Relationship 요소들. */
function relEls(doc: Document, pred?: (el: Element) => boolean): Element[] {
  const all = Array.from(doc.getElementsByTagNameNS(REL_NS, 'Relationship'));
  return pred ? all.filter(pred) : all;
}

/** 배경 변경 — p:bg 를 cSld 첫 자식으로 upsert(단색). 모델 배경이 단색이 아니면
 *  p:bg 제거(레이아웃/마스터 상속 복귀). CT_CommonSlideData 순서상 bg 는 spTree 앞. */
async function patchSlideBackground(
  xmlDoc: Document,
  cSld: Element,
  background: PptxSlide['background'],
  pres: PptxPresentation,
  slidePartPath: string,
): Promise<void> {
  const existing = Array.from(cSld.childNodes).find(
    (n) => n.nodeType === 1 && (n as Element).localName === 'bg' && (n as Element).namespaceURI === P_NS,
  ) as Element | undefined;
  if (background?.nativeSource) {
    const source = background.nativeSource;
    const parsed = new DOMParser().parseFromString(source.xml, 'application/xml');
    const root = parsed.documentElement;
    if (parsed.getElementsByTagName('parsererror').length || root.localName !== 'bg' || root.namespaceURI !== P_NS) {
      throw new Error('적용할 배경 원본을 읽을 수 없습니다.');
    }
    const imported = xmlDoc.importNode(root, true);
    const attributes = [imported, ...Array.from(imported.getElementsByTagName('*'))]
      .flatMap(el => Array.from(el.attributes)).filter(attribute => attribute.namespaceURI === R_NS);
    const images = new Map(source.images.map(image => [image.relationshipId, image.partPath]));
    if (attributes.some(attribute => attribute.localName !== 'embed' || !images.has(attribute.value))) {
      throw new Error('문서 내부 이미지로 확인할 수 없는 배경입니다.');
    }
    const relsPath = relsPathOf(slidePartPath);
    const originalRels = await readZipText(pres.zip, relsPath) ?? `<Relationships xmlns="${REL_NS}"/>`;
    const rels = new DOMParser().parseFromString(originalRels, 'application/xml');
    if (rels.getElementsByTagName('parsererror').length) throw new Error('슬라이드의 이미지 연결 정보를 읽을 수 없습니다.');
    const aliases = new Map<string, string>();
    for (const attribute of attributes) {
      const sourceId = attribute.value;
      if (!aliases.has(sourceId)) {
        const part = images.get(sourceId)!;
        if (!/^ppt\/media\/[^/]+$/u.test(part) || !pres.zip.file(part)) throw new Error('배경 이미지 원본을 찾을 수 없습니다.');
        const target = `/${part}`;
        const same = relEls(rels, rel => rel.getAttribute('Type') === `${R_NS}/image`
          && rel.getAttribute('Target') === target && rel.getAttribute('TargetMode') !== 'External')[0];
        let alias = same?.getAttribute('Id') ?? '';
        if (!alias) {
          let index = 1; const used = new Set(relEls(rels).map(rel => rel.getAttribute('Id')));
          while (used.has(`rIdDeckBackground${index}`)) index += 1;
          alias = `rIdDeckBackground${index}`;
          const rel = rels.createElementNS(REL_NS, 'Relationship');
          rel.setAttribute('Id', alias); rel.setAttribute('Type', `${R_NS}/image`); rel.setAttribute('Target', target);
          rels.documentElement.appendChild(rel);
        }
        aliases.set(sourceId, alias);
      }
      attribute.value = aliases.get(sourceId)!;
    }
    if (attributes.length) writeXmlPart(pres, relsPath, rels, originalRels);
    if (existing) cSld.replaceChild(imported, existing); else cSld.insertBefore(imported, cSld.firstChild);
    return;
  }
  const color = background?.fill?.type === 'solid' ? background.fill.color : undefined;
  const solidFill = color ? makeSolidFillFromColor(xmlDoc, color) : null;
  if (!solidFill) {
    if (existing) cSld.removeChild(existing);
    return;
  }
  let bg = existing;
  if (!bg) {
    bg = xmlDoc.createElementNS(P_NS, 'p:bg');
    cSld.insertBefore(bg, cSld.firstChild);
  }
  while (bg.firstChild) bg.removeChild(bg.firstChild);
  const bgPr = xmlDoc.createElementNS(P_NS, 'p:bgPr');
  bgPr.appendChild(solidFill);
  bgPr.appendChild(xmlDoc.createElementNS(A_NS, 'a:effectLst'));
  bg.appendChild(bgPr);
}

/** A saved proposal may be undone to a model without backgroundDirty. Remove
 * only our now-unreferenced image aliases while retaining every other relation. */
async function pruneUnusedDeckBackgroundRelations(pres: PptxPresentation, part: string, doc: Document): Promise<void> {
  const path = relsPathOf(part);
  const raw = await readZipText(pres.zip, path);
  if (!raw?.includes('rIdDeckBackground')) return;
  const used = new Set(Array.from(doc.getElementsByTagName('*')).flatMap(element => Array.from(element.attributes))
    .filter(attribute => attribute.namespaceURI === R_NS).map(attribute => attribute.value));
  const relations = new DOMParser().parseFromString(raw, 'application/xml');
  let changed = false;
  for (const relation of relEls(relations)) {
    const id = relation.getAttribute('Id') ?? '';
    if (/^rIdDeckBackground\d+$/u.test(id) && relation.getAttribute('Type') === `${R_NS}/image` && !used.has(id)) {
      relation.remove(); changed = true;
    }
  }
  if (changed) writeXmlPart(pres, path, relations, raw);
}

/** 레이아웃 적용 — 슬라이드 _rels 의 slideLayout rel Target 을 새 레이아웃으로 교체. */
async function patchSlideLayoutRel(
  pres: PptxPresentation,
  slidePartPath: string,
  layoutPartPath: string,
): Promise<void> {
  const relsPath = relsPathOf(slidePartPath);
  const xml = await readZipText(pres.zip, relsPath);
  if (!xml) return;
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const rel = relEls(doc, (e) => e.getAttribute('Type') === RELTYPE_LAYOUT)[0];
  if (!rel) return;
  rel.setAttribute('Target', `../${layoutPartPath.replace(/^ppt\//, '')}`);
  writeXmlPart(pres, relsPath, doc, xml);
}

/**
 * 슬라이드 목록 재조정 — 모델 slides[] 순서를 SoT 로:
 *  1) deletedSlideParts 파트 제거(모델에 되살아난 파트는 스킵 — undo 안전) + 노트/rels/CT 정리
 *  2) synthetic(빈)·duplicatedFromPart(복제) 슬라이드의 파트/_rels/CT/rels 생성,
 *     복제는 노트 파트까지 복제(역참조 rel 재작성 — PowerPoint 미러)
 *  3) sldIdLst 를 모델 순서로 재작성(기존 @id/r:id 보존, 신규는 max+1)
 * 멱등: partPath 부여 후 재호출 시 기존 파트로 취급된다.
 */
async function reconcilePresentation(pres: PptxPresentation, dirtySlideIds: Set<string>): Promise<void> {
  const zip = pres.zip;
  const presPath = 'ppt/presentation.xml';
  const relsPath = 'ppt/_rels/presentation.xml.rels';
  const ctPath = '[Content_Types].xml';
  const presXml = await readZipText(zip, presPath);
  const relsXml = await readZipText(zip, relsPath);
  const ctXml = await readZipText(zip, ctPath);
  if (!presXml || !relsXml || !ctXml) return;

  const presDoc = new DOMParser().parseFromString(presXml, 'application/xml');
  const relsDoc = new DOMParser().parseFromString(relsXml, 'application/xml');
  const ctDoc = new DOMParser().parseFromString(ctXml, 'application/xml');
  const sldIdLst = presDoc.getElementsByTagNameNS(P_NS, 'sldIdLst')[0];
  const relsRoot = relsDoc.documentElement;
  const ctRoot = ctDoc.documentElement;
  if (!sldIdLst || !relsRoot || !ctRoot) return;

  const archives = pres.slideArchiveSnapshots ??= new Map();
  const parseElement = (xml: string) => new DOMParser().parseFromString(xml, 'application/xml').documentElement;
  // 삭제 후 저장된 ZIP에는 파트뿐 아니라 sldId/rels/CT도 없다. undo로 모델에
  // 돌아온 슬라이드의 패키지 전체를 인벤토리를 만들기 전에 복구한다.
  for (const slide of pres.slides) {
    if (!slide.partPath || zip.file(slide.partPath)) continue;
    const archived = archives.get(slide.partPath);
    if (!archived) continue;
    for (const part of archived.parts) {
      zip.file(part.path, part.xml, { createFolders: false });
      if (part.rawXml !== undefined) pres.rawParts.set(part.path, part.rawXml);
      if (part.contentTypeXml) ctRoot.appendChild(ctDoc.importNode(parseElement(part.contentTypeXml), true));
    }
    relsRoot.appendChild(relsDoc.importNode(parseElement(archived.relationshipXml), true));
    sldIdLst.appendChild(presDoc.importNode(parseElement(archived.slideIdXml), true));
  }

  // ---- 인벤토리: rId→partPath, partPath→기존 sldId 요소, 각종 max ----
  const relByRid = new Map<string, Element>();
  let maxRid = 0;
  for (const rel of relEls(relsDoc)) {
    const rid = rel.getAttribute('Id') || '';
    relByRid.set(rid, rel);
    const m = /^rId(\d+)$/.exec(rid);
    if (m) maxRid = Math.max(maxRid, Number(m[1]));
  }
  const partOfRid = (rid: string): string | null => {
    const rel = relByRid.get(rid);
    const t = rel?.getAttribute('Target');
    return t ? (t.startsWith('/') ? t.slice(1) : `ppt/${t}`) : null;
  };
  const sldIdByPart = new Map<string, Element>();
  let maxSldId = 255; // sldId@id 는 256 이상 규약
  for (const el of Array.from(sldIdLst.getElementsByTagNameNS(P_NS, 'sldId'))) {
    const id = Number(el.getAttribute('id'));
    if (Number.isFinite(id)) maxSldId = Math.max(maxSldId, id);
    const rid = el.getAttributeNS(R_NS, 'id') || el.getAttribute('r:id') || '';
    const part = partOfRid(rid);
    if (part) sldIdByPart.set(part, el);
  }
  let maxSlideNum = 0;
  let maxNotesNum = 0;
  zip.forEach((p) => {
    let m = /^ppt\/slides\/slide(\d+)\.xml$/.exec(p);
    if (m) maxSlideNum = Math.max(maxSlideNum, Number(m[1]));
    m = /^ppt\/notesSlides\/notesSlide(\d+)\.xml$/.exec(p);
    if (m) maxNotesNum = Math.max(maxNotesNum, Number(m[1]));
  });
  // 제거한 최고 번호도 예약한다. 재사용하면 나중 undo가 새 슬라이드/노트를
  // 같은 partPath로 오인하거나 sldId/rId가 다른 관계와 충돌할 수 있다.
  for (const archived of archives.values()) {
    for (const part of archived.parts) {
      let match = /^ppt\/slides\/slide(\d+)\.xml$/.exec(part.path);
      if (match) maxSlideNum = Math.max(maxSlideNum, Number(match[1]));
      match = /^ppt\/notesSlides\/notesSlide(\d+)\.xml$/.exec(part.path);
      if (match) maxNotesNum = Math.max(maxNotesNum, Number(match[1]));
    }
    const rid = /^rId(\d+)$/.exec(parseElement(archived.relationshipXml).getAttribute('Id') || '');
    if (rid) maxRid = Math.max(maxRid, Number(rid[1]));
    const slideId = Number(parseElement(archived.slideIdXml).getAttribute('id'));
    if (Number.isFinite(slideId)) maxSldId = Math.max(maxSldId, slideId);
  }

  const ctOverrideOf = (partName: string): Element | null =>
    Array.from(ctDoc.getElementsByTagNameNS(CT_NS, 'Override'))
      .find((o) => o.getAttribute('PartName') === partName) ?? null;
  const addCtOverride = (partPath: string, contentType: string) => {
    if (ctOverrideOf(`/${partPath}`)) return;
    const o = ctDoc.createElementNS(CT_NS, 'Override');
    o.setAttribute('PartName', `/${partPath}`);
    o.setAttribute('ContentType', contentType);
    ctRoot.appendChild(o);
  };
  /** 파트의 _rels 에서 노트 슬라이드 partPath (없으면 null). */
  const notesPartOf = async (slidePart: string): Promise<string | null> => {
    const xml = await readZipText(zip, relsPathOf(slidePart));
    if (!xml) return null;
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    const rel = relEls(doc, (e) => e.getAttribute('Type') === RELTYPE_NOTES)[0];
    return rel ? slideRelTargetToPart(rel.getAttribute('Target') || '') : null;
  };
  const retainSlidePart = async (partPath: string) => {
    const sldEl = sldIdByPart.get(partPath);
    const rid = sldEl?.getAttributeNS(R_NS, 'id') || sldEl?.getAttribute('r:id') || '';
    const rel = relByRid.get(rid);
    const xml = await readZipText(zip, partPath);
    if (!sldEl || !rel || !xml) return;
    const notesPart = await notesPartOf(partPath);
    const paths = [partPath, relsPathOf(partPath), ...(notesPart ? [notesPart, relsPathOf(notesPart)] : [])];
    const parts: PptxSlideArchiveSnapshot['parts'] = [];
    const serializer = new XMLSerializer();
    for (const path of paths) {
      const partXml = await readZipText(zip, path);
      if (partXml === null) continue;
      const override = ctOverrideOf(`/${path}`);
      parts.push({
        path, xml: partXml, rawXml: pres.rawParts.get(path),
        contentTypeXml: override ? serializer.serializeToString(override) : undefined,
      });
    }
    const textAt = (path: string) => parts.find((part) => part.path === path)?.xml ?? null;
    archives.set(partPath, {
      parts,
      slideIdXml: serializer.serializeToString(sldEl),
      relationshipXml: serializer.serializeToString(rel),
      source: {
        xml: pres.rawParts.get(partPath) ?? xml,
        relsXml: textAt(relsPathOf(partPath)),
        notesXml: notesPart ? textAt(notesPart) : null,
        notesRelsXml: notesPart ? textAt(relsPathOf(notesPart)) : null,
      },
    });
  };
  const removePart = (partPath: string) => {
    zip.remove(partPath);
    zip.remove(relsPathOf(partPath));
    pres.rawParts.delete(partPath);
    const o = ctOverrideOf(`/${partPath}`);
    if (o) o.parentNode?.removeChild(o);
  };

  // ---- 0) 복제 소스 선캡처 — 같은 저장에서 원본이 삭제될 수 있다(복제 후 원본
  //      삭제). 삭제 패스가 파트/rawParts 를 지우기 전에 원본 바이트(+rels+노트)를
  //      확보해 복제본 소실을 막는다.
  interface DupSource {
    xml: string; relsXml: string | null;
    notesXml: string | null; notesRelsXml: string | null;
  }
  const dupSources = new Map<string, DupSource>();
  for (const slide of pres.slides) {
    const src = slide.duplicatedFromPart;
    if (slide.partPath || !src || dupSources.has(src)) continue;
    if (slide.duplicateSourceSnapshot) continue; // 클립보드 스냅샷 보유 — 캡처 불필요
    const archivedSource = !zip.file(src) ? archives.get(src)?.source : undefined;
    if (archivedSource) {
      dupSources.set(src, {
        xml: archivedSource.xml,
        relsXml: archivedSource.relsXml ?? null,
        notesXml: archivedSource.notesXml ?? null,
        notesRelsXml: archivedSource.notesRelsXml ?? null,
      });
      continue;
    }
    const xml = pres.rawParts.get(src) ?? await readZipText(zip, src);
    if (!xml) continue;
    const relsXml = await readZipText(zip, relsPathOf(src));
    let notesXml: string | null = null;
    let notesRelsXml: string | null = null;
    const notesPart = await notesPartOf(src);
    if (notesPart) {
      notesXml = await readZipText(zip, notesPart);
      notesRelsXml = await readZipText(zip, relsPathOf(notesPart));
    }
    dupSources.set(src, { xml, relsXml, notesXml, notesRelsXml });
  }

  // ---- 1) 명시 삭제(모델에 되살아난 파트는 보존 — undo 안전) ----
  const livePartPaths = new Set(pres.slides.map((s) => s.partPath).filter(Boolean));
  for (const part of pres.deletedSlideParts ?? []) {
    if (livePartPaths.has(part) || !zip.file(part)) continue;
    await retainSlidePart(part);
    const notes = await notesPartOf(part);
    // 노트는 슬라이드와 1:1 — 함께 삭제(PowerPoint 미러)
    if (notes && zip.file(notes)) removePart(notes);
    removePart(part);
    const sldEl = sldIdByPart.get(part);
    if (sldEl) {
      const rid = sldEl.getAttributeNS(R_NS, 'id') || sldEl.getAttribute('r:id') || '';
      const rel = relByRid.get(rid);
      if (rel) rel.parentNode?.removeChild(rel);
      sldEl.parentNode?.removeChild(sldEl);
      sldIdByPart.delete(part);
    }
  }

  // ---- 2) 신규(synthetic)/복제(duplicatedFromPart) 파트 생성 ----
  for (const slide of pres.slides) {
    if (slide.partPath) continue; // 기존 파트(또는 이전 저장에서 이미 생성) — 멱등
    const newNum = ++maxSlideNum;
    const newPart = `ppt/slides/slide${newNum}.xml`;

    // 복제 소스 우선순위: 클립보드 스냅샷(복사 시점 바이트 — 원본이 이미 삭제/저장
    // 됐어도 유효) > 같은 저장의 선캡처(dupSources).
    const snap = slide.duplicateSourceSnapshot;
    const dupSrc: DupSource | undefined = snap
      ? {
          xml: snap.xml,
          relsXml: snap.relsXml ?? null,
          notesXml: snap.notesXml ?? null,
          notesRelsXml: snap.notesRelsXml ?? null,
        }
      : (slide.duplicatedFromPart ? dupSources.get(slide.duplicatedFromPart) : undefined);
    if (dupSrc) {
      // 복제: 선캡처한 원본 파트의 **파스 시점 바이트**를 복제 — 복제 모델이 가진
      // dirty 편집분은 per-slide 루프가 이 복제 파트 위에 그대로 패치한다.
      // (선캡처라 같은 저장에서 원본이 삭제돼도 복제본은 온전하다.)
      zip.file(newPart, dupSrc.xml, { createFolders: false });
      pres.rawParts.set(newPart, dupSrc.xml);
      if (dupSrc.relsXml) {
        const relsDocNew = new DOMParser().parseFromString(dupSrc.relsXml, 'application/xml');
        // 노트 복제(PowerPoint 미러): 새 노트 파트 + 역참조(slide rel) 재작성
        const notesRel = relEls(relsDocNew, (e) => e.getAttribute('Type') === RELTYPE_NOTES)[0];
        if (notesRel) {
          if (dupSrc.notesXml) {
            const newNotes = `ppt/notesSlides/notesSlide${++maxNotesNum}.xml`;
            zip.file(newNotes, dupSrc.notesXml, { createFolders: false });
            if (dupSrc.notesRelsXml) {
              const nDoc = new DOMParser().parseFromString(dupSrc.notesRelsXml, 'application/xml');
              const backRel = relEls(nDoc, (e) => e.getAttribute('Type') === RELTYPE_SLIDE)[0];
              if (backRel) backRel.setAttribute('Target', `../slides/slide${newNum}.xml`);
              writeXmlPart(pres, relsPathOf(newNotes), nDoc, dupSrc.notesRelsXml);
            }
            notesRel.setAttribute('Target', `../${newNotes.replace(/^ppt\//, '')}`);
            addCtOverride(newNotes, CT_NOTES);
          } else {
            notesRel.parentNode?.removeChild(notesRel); // 원본 노트 파트 유실 — rel 제거
          }
        }
        writeXmlPart(pres, relsPathOf(newPart), relsDocNew, dupSrc.relsXml);
      }
    } else if (slide.synthetic) {
      // 새 빈 슬라이드: 빈 spTree 파트 + 레이아웃 rel. 모델의 placeholder 도형은
      // structuralDirty 경로(reconcileShapeTree)가 채우므로 dirty 를 보장한다.
      const layout = slide.layoutRef && pres.slideLayouts.some((l) => l.partPath === slide.layoutRef)
        ? slide.layoutRef
        : pres.slideLayouts[0]?.partPath;
      const xml = blankSlideXml();
      zip.file(newPart, xml, { createFolders: false });
      pres.rawParts.set(newPart, xml);
      if (layout) {
        zip.file(relsPathOf(newPart), blankSlideRelsXml(layout), { createFolders: false });
      }
      dirtySlideIds.add(slide.id);
    } else {
      continue; // partPath 없는데 synthetic/복제도 아님 — 방어적 스킵
    }

    slide.partPath = newPart;
    // 미저장 새 슬라이드에 삽입된 이미지의 렌더 media 키(''::rId)를 새 파트로 이관.
    for (const s of slide.shapes) {
      if (s.imageRId) {
        const v = pres.media.get(`::${s.imageRId}`);
        if (v !== undefined) pres.media.set(`${newPart}::${s.imageRId}`, v);
      }
    }
    livePartPaths.add(newPart);
    addCtOverride(newPart, CT_SLIDE);
    const rid = `rId${++maxRid}`;
    const rel = relsDoc.createElementNS(REL_NS, 'Relationship');
    rel.setAttribute('Id', rid);
    rel.setAttribute('Type', RELTYPE_SLIDE);
    rel.setAttribute('Target', slidePartToPresRel(newPart));
    relsRoot.appendChild(rel);
    relByRid.set(rid, rel);
    const sldEl = presDoc.createElementNS(P_NS, 'p:sldId');
    sldEl.setAttribute('id', String(++maxSldId));
    sldEl.setAttributeNS(R_NS, 'r:id', rid);
    sldIdByPart.set(newPart, sldEl);
  }

  // ---- 3) sldIdLst 를 모델 순서로 재작성(기존 요소 이동, @id/r:id 보존) ----
  const ordered: Element[] = [];
  for (const slide of pres.slides) {
    const el = slide.partPath ? sldIdByPart.get(slide.partPath) : undefined;
    if (el) ordered.push(el);
  }
  // 모델에 없는 잔여 sldId 처리:
  //  - 파스 시점부터 있던 파트(sldIdLstParts — 파스실패 슬라이드 등)는 보존.
  //  - 그 외는 **이전 저장이 만든 뒤 undo 로 모델에서 사라진 고스트** — sldId 만
  //    남기면 덱에 유령 슬라이드가 보이므로 파트/rels/CT/rel 까지 완전 제거한다.
  const parseTimeParts = new Set(pres.sldIdLstParts ?? []);
  for (const [part, el] of sldIdByPart) {
    if (ordered.includes(el)) continue;
    if ((pres.deletedSlideParts ?? []).includes(part)) continue; // 삭제 패스가 이미 처리
    if (parseTimeParts.has(part)) { ordered.push(el); continue; }
    const rid = el.getAttributeNS(R_NS, 'id') || el.getAttribute('r:id') || '';
    const rel = relByRid.get(rid);
    await retainSlidePart(part);
    if (rel) rel.parentNode?.removeChild(rel);
    const ghostNotes = await notesPartOf(part);
    if (ghostNotes && zip.file(ghostNotes)) removePart(ghostNotes);
    if (zip.file(part)) removePart(part);
  }
  while (sldIdLst.firstChild) sldIdLst.removeChild(sldIdLst.firstChild);
  for (const el of ordered) sldIdLst.appendChild(el);

  writeXmlPart(pres, presPath, presDoc, presXml);
  writeXmlPart(pres, relsPath, relsDoc, relsXml);
  writeXmlPart(pres, ctPath, ctDoc, ctXml);
}

/* ------------------------------------------------------------------ */
/* ShapeTree 패치 — cNvPr-id 앵커링                                     */
/* ------------------------------------------------------------------ */

/** 패처가 다루는 도형 컨테이너 요소 localName */
const SHAPE_ELEMENTS = new Set(['sp', 'pic', 'cxnSp', 'grpSp', 'graphicFrame']);

/** shape.type → 대응 XML 요소 localName (positional 폴백용) */
function xmlLocalNameForShape(shape: PptxShape): string {
  switch (shape.type) {
    case 'connector': return 'cxnSp';
    case 'image': return 'pic';
    case 'group': return 'grpSp';
    case 'table':
    case 'chart': return 'graphicFrame';
    default: return 'sp'; // textbox | shape
  }
}

/** 도형 요소의 자기 cNvPr@id 를 순수 숫자로. 없거나 비숫자면 null.
 *  (nv*Pr 이 요소의 첫 자식이라 getElementsByTagNameNS[0] 이 컨테이너 자신의
 *   cNvPr — 그룹의 경우 자식 도형 cNvPr 보다 문서순 앞섬 — 을 준다.) */
function elementCNvId(el: Element): number | null {
  const cNvPr = el.getElementsByTagNameNS(P_NS, 'cNvPr')[0];
  const raw = cNvPr ? cNvPr.getAttribute('id') : null;
  return raw && /^\d+$/.test(raw) ? parseInt(raw, 10) : null;
}

/** el 의 직계 자식 중 첫 (ns, localName) 요소. getElementsByTagNameNS(재귀)와 달리
 *  자손을 안 봐 그룹/표의 자식 spPr 등을 오탐하지 않는다. */
function directChildNS(el: Element, ns: string, localName: string): Element | null {
  for (let i = 0; i < el.childNodes.length; i++) {
    const c = el.childNodes[i];
    if (c.nodeType === 1 && (c as Element).namespaceURI === ns && (c as Element).localName === localName) {
      return c as Element;
    }
  }
  return null;
}

/** 요소가 placeholder(nv*Pr>nvPr>ph)를 가지는가 — 파서는 xfrm 없어도 placeholder 면
 *  모델에 담는다(sp 는 nvSpPr, pic 는 nvPicPr 등 컨테이너가 다르므로 일반 탐색). */
function hasPlaceholder(el: Element): boolean {
  for (let i = 0; i < el.childNodes.length; i++) {
    const c = el.childNodes[i];
    if (c.nodeType !== 1) continue;
    const ce = c as Element;
    if (ce.namespaceURI === P_NS && ce.localName.startsWith('nv')) {
      const nvPr = directChildNS(ce, P_NS, 'nvPr');
      return !!nvPr && !!directChildNS(nvPr, P_NS, 'ph');
    }
  }
  return false;
}

/** spPr>a:xfrm 이 off·ext 를 모두 가지는가 — parseTransform 은 둘 다 있어야 유효로 본다
 *  (빈 <a:xfrm/> 는 드롭). grpSp 는 grpSpPr, graphicFrame 은 p:xfrm/a:xfrm 직계. */
function hasRealXfrm(el: Element): boolean {
  let xfrm: Element | null = null;
  if (el.localName === 'graphicFrame') {
    xfrm = directChildNS(el, P_NS, 'xfrm') ?? directChildNS(el, A_NS, 'xfrm');
  } else {
    const container = el.localName === 'grpSp'
      ? directChildNS(el, P_NS, 'grpSpPr') : directChildNS(el, P_NS, 'spPr');
    xfrm = container ? directChildNS(container, A_NS, 'xfrm') : null;
  }
  return !!xfrm && !!directChildNS(xfrm, A_NS, 'off') && !!directChildNS(xfrm, A_NS, 'ext');
}

/**
 * 이 요소가 파서가 모델에서 드롭할 요소로 보이는가.
 * 파서 드롭 규칙: xfrm(off+ext) 없고 placeholder 도 없는 sp/pic/cxnSp / tbl·chart 가
 * 아닌 graphicFrame(SmartArt/OLE 등). positional 폴백이 이런 드롭 요소를 모델 도형에
 * 오매칭해 내용을 파괴하고 실제 요소를 유령으로 남기는 것을 막는다(중복/누락 cNvId 덱).
 */
function looksParserDropped(el: Element): boolean {
  if (el.namespaceURI !== P_NS) return false;
  const ln = el.localName;
  if (ln === 'sp' || ln === 'pic' || ln === 'cxnSp') {
    return !hasRealXfrm(el) && !hasPlaceholder(el);
  }
  if (ln === 'grpSp') {
    // 파서는 xfrm 없고 자식도 없는 빈 그룹을 드롭한다.
    return !hasRealXfrm(el) && !firstShapeChild(el);
  }
  if (ln === 'graphicFrame') {
    // 드롭이 확실한 것만 표시(과드롭 회피): SmartArt(diagram)·OLE. tbl/chart 및 판정
    // 불확실(size0 chart·chartex 등)은 보존측(false)으로 둬 실제 콘텐츠 오드롭을 막는다.
    const graphic = directChildNS(el, A_NS, 'graphic');
    const gd = graphic ? directChildNS(graphic, A_NS, 'graphicData') : null;
    const uri = gd?.getAttribute('uri') || '';
    return uri.includes('/diagram') || uri.includes('oleObject') || uri.includes('/ole');
  }
  return false;
}

/** el 의 직계 자식 중 첫 도형 요소(SHAPE_ELEMENTS). */
function firstShapeChild(el: Element): Element | null {
  for (let i = 0; i < el.childNodes.length; i++) {
    const c = el.childNodes[i];
    if (c.nodeType === 1 && (c as Element).namespaceURI === P_NS && SHAPE_ELEMENTS.has((c as Element).localName)) {
      return c as Element;
    }
  }
  return null;
}

/** 모델 색 문자열(#hex / rgb() / rgba())을 srgbClr@val hex + alpha 로. 무효(named/
 *  transparent)면 null → 색을 굽지 않아(테마 상속) 무효 val 로 파일이 깨지는 것을 막는다. */
function parseColorHexAlpha(color?: string): { hex: string; alpha?: number } | null {
  if (!color) return null;
  let c = color.trim();
  if (c.startsWith('#')) c = c.slice(1);
  if (/^[0-9a-fA-F]{6}$/.test(c)) return { hex: c.toUpperCase() };
  if (/^[0-9a-fA-F]{3}$/.test(c)) return { hex: c.split('').map((x) => x + x).join('').toUpperCase() };
  const m = c.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)$/i);
  if (m) {
    const to2 = (n: string) => Math.max(0, Math.min(255, parseInt(n, 10))).toString(16).padStart(2, '0');
    const hex = (to2(m[1]) + to2(m[2]) + to2(m[3])).toUpperCase();
    const alpha = m[4] !== undefined ? Math.max(0, Math.min(1, parseFloat(m[4]))) : undefined;
    return { hex, alpha };
  }
  return null;
}

/** solidFill 요소 내용을 색(srgbClr val + 필요 시 a:alpha)으로 교체. 유효색이면 true. */
function setSolidFillColor(xmlDoc: Document, solidFill: Element, color?: string): boolean {
  const parsed = parseColorHexAlpha(color);
  if (!parsed) return false;
  while (solidFill.firstChild) solidFill.removeChild(solidFill.firstChild);
  const srgb = xmlDoc.createElementNS(A_NS, 'a:srgbClr');
  srgb.setAttribute('val', parsed.hex);
  if (parsed.alpha !== undefined && parsed.alpha < 1) {
    const alpha = xmlDoc.createElementNS(A_NS, 'a:alpha');
    alpha.setAttribute('val', String(Math.round(parsed.alpha * 100000)));
    srgb.appendChild(alpha);
  }
  solidFill.appendChild(srgb);
  return true;
}

/**
 * 도형 요소의 위치/크기/회전(xfrm)을 타입별 올바른 위치에 되쓴다:
 *  - sp/pic/cxnSp → spPr > a:xfrm
 *  - grpSp        → grpSpPr > a:xfrm (chOff/chExt 는 보존)
 *  - graphicFrame → p:xfrm (직계, a:xfrm 아님)
 * (종전엔 sp/textbox/connector 만 되써 그룹/이미지/표/차트 이동이 무음 유실됐다.)
 */
function patchTransform(xmlDoc: Document, el: Element, shape: PptxShape): void {
  const ln = el.localName;
  if (ln === 'graphicFrame') {
    // 기존 p:xfrm 우선, 없으면 (비표준) 직계 a:xfrm 재사용 — 둘 다 없을 때만 생성.
    // (a:xfrm 을 못 보고 p:xfrm 을 새로 만들면 xfrm 이 2개가 돼 스키마 위반.)
    let xfrm = directChildNS(el, P_NS, 'xfrm') ?? directChildNS(el, A_NS, 'xfrm');
    if (!xfrm) {
      xfrm = xmlDoc.createElementNS(P_NS, 'p:xfrm');
      const nv = directChildNS(el, P_NS, 'nvGraphicFramePr');
      if (nv && nv.nextSibling) el.insertBefore(xfrm, nv.nextSibling);
      else if (nv) el.appendChild(xfrm);
      else el.insertBefore(xfrm, el.firstChild);
    }
    writeXfrm(xmlDoc, xfrm, shape);
    return;
  }
  const container = ln === 'grpSp' ? directChildNS(el, P_NS, 'grpSpPr') : directChildNS(el, P_NS, 'spPr');
  if (!container) return;
  let xfrm = container.getElementsByTagNameNS(A_NS, 'xfrm')[0];
  if (!xfrm) {
    xfrm = xmlDoc.createElementNS(A_NS, 'a:xfrm');
    container.insertBefore(xfrm, container.firstChild);
  }
  writeXfrm(xmlDoc, xfrm, shape);
  // 그룹은 자식 좌표계(chOff/chExt)도 갱신 — 자식 이동으로 그룹 박스가 재계산되면
  // 이걸 안 쓰면 재파싱 시 자식이 옛 좌표계로 remap 돼 어긋난다.
  if (ln === 'grpSp' && (shape.groupChildOffset || shape.groupChildExtent)) {
    writeGroupChildFrame(xmlDoc, xfrm, shape);
  }
}

/** prstGeom/avLst 의 gd 조정값을 모델 adjustments 로 되쓴다(커넥터 꺾임 등).
 *  - spPr > prstGeom > avLst 를 찾고(없으면 avLst 생성, prstGeom 첫 자식),
 *    각 name 의 gd 를 `val <int>` fmla 로 갱신/생성한다.
 *  - custGeom(prstGeom 없음)이면 스킵 — avLst 는 prstGeom 전용이다. */
function patchAdjustments(
  xmlDoc: Document,
  spEl: Element,
  adjustments: Record<string, number>,
): void {
  const spPr = directChildNS(spEl, P_NS, 'spPr');
  if (!spPr) return;
  const prstGeom = directChildNS(spPr, A_NS, 'prstGeom');
  if (!prstGeom) return; // custGeom/무지오메트리 — avLst 대상 아님
  let avLst = directChildNS(prstGeom, A_NS, 'avLst');
  if (!avLst) {
    avLst = xmlDoc.createElementNS(A_NS, 'a:avLst');
    prstGeom.insertBefore(avLst, prstGeom.firstChild); // prstGeom 의 유일 자식
  }
  for (const [name, val] of Object.entries(adjustments)) {
    if (!Number.isFinite(val)) continue;
    let gd: Element | null = null;
    const gds = avLst.getElementsByTagNameNS(A_NS, 'gd');
    for (let i = 0; i < gds.length; i++) {
      if (gds[i].getAttribute('name') === name) { gd = gds[i]; break; }
    }
    if (!gd) {
      gd = xmlDoc.createElementNS(A_NS, 'a:gd');
      gd.setAttribute('name', name);
      avLst.appendChild(gd);
    }
    gd.setAttribute('fmla', `val ${Math.round(val)}`);
  }
}

/** 그룹 xfrm 의 a:chOff(x/y)/a:chExt(cx/cy) 를 모델 groupChildOffset/Extent 로 되쓴다.
 *  순서: off → ext → chOff → chExt (기존 요소는 attr 갱신, 없으면 순서대로 삽입). */
function writeGroupChildFrame(xmlDoc: Document, xfrm: Element, shape: PptxShape): void {
  const ext = xfrm.getElementsByTagNameNS(A_NS, 'ext')[0];
  if (shape.groupChildOffset) {
    let chOff = directChildNS(xfrm, A_NS, 'chOff');
    if (!chOff) {
      chOff = xmlDoc.createElementNS(A_NS, 'a:chOff');
      if (ext && ext.nextSibling) xfrm.insertBefore(chOff, ext.nextSibling);
      else xfrm.appendChild(chOff);
    }
    chOff.setAttribute('x', String(pxToEmu(shape.groupChildOffset.x)));
    chOff.setAttribute('y', String(pxToEmu(shape.groupChildOffset.y)));
  }
  if (shape.groupChildExtent) {
    let chExt = directChildNS(xfrm, A_NS, 'chExt');
    if (!chExt) {
      chExt = xmlDoc.createElementNS(A_NS, 'a:chExt');
      const chOff = directChildNS(xfrm, A_NS, 'chOff');
      if (chOff && chOff.nextSibling) xfrm.insertBefore(chExt, chOff.nextSibling);
      else xfrm.appendChild(chExt);
    }
    chExt.setAttribute('cx', String(pxToEmu(shape.groupChildExtent.width)));
    chExt.setAttribute('cy', String(pxToEmu(shape.groupChildExtent.height)));
  }
}

/** xfrm 요소에 rot/flip 속성 + a:off + a:ext 를 되쓴다(chOff/chExt 등 나머지 자식 보존). */
function writeXfrm(xmlDoc: Document, xfrm: Element, shape: PptxShape): void {
  if (shape.rotation) xfrm.setAttribute('rot', String(Math.round(shape.rotation * 60000)));
  else xfrm.removeAttribute('rot');
  if (shape.flipH) xfrm.setAttribute('flipH', '1'); else xfrm.removeAttribute('flipH');
  if (shape.flipV) xfrm.setAttribute('flipV', '1'); else xfrm.removeAttribute('flipV');

  let off = xfrm.getElementsByTagNameNS(A_NS, 'off')[0];
  if (!off) {
    off = xmlDoc.createElementNS(A_NS, 'a:off');
    xfrm.insertBefore(off, xfrm.firstChild);
  }
  off.setAttribute('x', String(pxToEmu(shape.position.x)));
  off.setAttribute('y', String(pxToEmu(shape.position.y)));

  let ext = xfrm.getElementsByTagNameNS(A_NS, 'ext')[0];
  if (!ext) {
    ext = xmlDoc.createElementNS(A_NS, 'a:ext');
    if (off.nextSibling) xfrm.insertBefore(ext, off.nextSibling);
    else xfrm.appendChild(ext);
  }
  ext.setAttribute('cx', String(pxToEmu(shape.size.width)));
  ext.setAttribute('cy', String(pxToEmu(shape.size.height)));
}

/**
 * spTree(또는 grpSp) 컨테이너를 cNvPr-id 앵커로 패치한다.
 *
 * 각 모델 도형을 cNvId(슬라이드 파트 내 유일)로 XML 요소에 매칭한다.
 * - 중복 cNvId(AI생성/복붙 덱의 malformed) → map 에서 제외하고 형별 positional
 *   폴백. 안 하면 중복 id 가 조용히 덮여 새 무음손상 클래스를 만든다.
 * - cNvId=null(anchorless, genId 폴백) 도형 → 형별 positional 폴백.
 * - group → 매칭된 grpSp 로 내려가 재귀.
 *
 * (종전 구현은 최상위 sp/pic/cxnSp 만 단일 인덱스로 매칭했는데, group/table 이
 *  인덱스만 전진시켜 그룹 뒤 도형 편집이 엉뚱한 요소에 착지·드롭되는 능동적
 *  무음손상이 있었다 — 그룹/표 포함 슬라이드 전부 위험.)
 */
function patchShapeTree(xmlDoc: Document, containerEl: Element, shapes: PptxShape[]): void {
  // 직계 도형 요소를 형별 문서순 리스트 + cNvId map 으로 수집
  const byType = new Map<string, Element[]>();
  const idMap = new Map<number, Element>();
  const conflicted = new Set<number>();
  for (let i = 0; i < containerEl.childNodes.length; i++) {
    const c = containerEl.childNodes[i];
    if (c.nodeType !== 1) continue;
    const el = c as Element;
    if (el.namespaceURI !== P_NS || !SHAPE_ELEMENTS.has(el.localName)) continue;
    let list = byType.get(el.localName);
    if (!list) { list = []; byType.set(el.localName, list); }
    list.push(el);
    const id = elementCNvId(el);
    if (id !== null) {
      if (idMap.has(id)) conflicted.add(id);
      else idMap.set(id, el);
    }
  }

  const consumed = new Set<Element>();
  const cursor = new Map<string, number>();
  // positional 폴백은 파서가 드롭할 요소(xfrm없는 sp/pic)를 건너뛴다 — 모델 도형이
  // 드롭 요소에 오착지해 내용을 파괴하는 것을 막는다(중복/누락 cNvId 덱).
  const nextPositional = (ln: string): Element | null => {
    const list = byType.get(ln);
    if (!list) return null;
    let i = cursor.get(ln) ?? 0;
    while (i < list.length && (consumed.has(list[i]) || looksParserDropped(list[i]))) i++;
    cursor.set(ln, i + 1);
    return i < list.length ? list[i] : null;
  };

  for (const shape of shapes) {
    let el: Element | null = null;

    // 1) cNvId 앵커 매칭 (유일·미충돌·미소비)
    if (shape.cNvId !== null && shape.cNvId !== undefined && !conflicted.has(shape.cNvId)) {
      const cand = idMap.get(shape.cNvId);
      if (cand && !consumed.has(cand)) el = cand;
    }
    // 2) 폴백: 형별 문서순 positional (anchorless / 중복 / 미매칭)
    if (!el) el = nextPositional(xmlLocalNameForShape(shape));
    if (!el) continue;
    consumed.add(el);
    dispatchMatchedShape(xmlDoc, el, shape, (child, children) =>
      patchShapeTree(xmlDoc, child, children));
  }
}

/**
 * 매칭된 도형 요소에 편집을 적용한다(patchShapeTree/reconcile 공통).
 *  - group  → transformDirty 면 그룹 자신의 xfrm 되쓰고, 자식은 recurse 로 위임.
 *  - 그 외(sp/textbox/pic/cxnSp/graphicFrame) → patchShapeElement(text/xfrm/fill).
 * (종전엔 image/table/chart 를 consume 만 해 이동/변형이 무음 유실됐다.)
 */
function dispatchMatchedShape(
  xmlDoc: Document,
  el: Element,
  shape: PptxShape,
  recurse: (child: Element, children: PptxShape[]) => void,
): void {
  if (shape.type === 'group') {
    if (shape.transformDirty) patchTransform(xmlDoc, el, shape);
    recurse(el, shape.children ?? []);
  } else {
    patchShapeElement(xmlDoc, el, shape);
    patchPptxImageProperties(xmlDoc, el, shape);
  }
}

/* ------------------------------------------------------------------ */
/* 구조편집 재구성 — 추가/삭제/복제/z-order                             */
/* ------------------------------------------------------------------ */

/** spTree 의 도형(sp/pic/…) 앞 head 마지막 요소(nvGrpSpPr/grpSpPr). 도형은 이
 *  뒤에 삽입한다. grpSpPr 이 nvGrpSpPr 보다 문서순 뒤(스키마)라 우선. */
function findShapeInsertAnchor(spTree: Element): Element | null {
  let nvGrpSpPr: Element | null = null;
  let grpSpPr: Element | null = null;
  for (let i = 0; i < spTree.childNodes.length; i++) {
    const c = spTree.childNodes[i];
    if (c.nodeType !== 1) continue;
    const el = c as Element;
    if (el.namespaceURI !== P_NS) continue;
    if (el.localName === 'nvGrpSpPr') nvGrpSpPr = el;
    else if (el.localName === 'grpSpPr') grpSpPr = el;
  }
  return grpSpPr ?? nvGrpSpPr;
}

/** 파트 전체(spTree 재귀 포함)의 cNvId → 도형 요소 맵. 중복 id 는 신뢰 불가라
 *  제외(컨테이너 스코프 conflicted 와 동일 규칙). duplicatedFrom 전역 폴백용. */
function buildGlobalIdMap(spTree: Element): Map<number, Element> {
  const map = new Map<number, Element>();
  const dupes = new Set<number>();
  for (const name of SHAPE_ELEMENTS) {
    const els = spTree.getElementsByTagNameNS(P_NS, name);
    for (let i = 0; i < els.length; i++) {
      const id = elementCNvId(els[i]);
      if (id === null) continue;
      if (map.has(id)) { dupes.add(id); continue; }
      map.set(id, els[i]);
    }
  }
  for (const id of dupes) map.delete(id);
  return map;
}

/** 컨테이너(spTree/grpSp) 의 tail 시작 요소(첫 extLst). 없으면 null. */
function findTailElement(container: Element, afterAnchor: Element | null): Element | null {
  let n: Node | null = afterAnchor ? afterAnchor.nextSibling : container.firstChild;
  while (n) {
    if (n.nodeType === 1) {
      const el = n as Element;
      if (el.namespaceURI === P_NS && el.localName === 'extLst') return el;
    }
    n = n.nextSibling;
  }
  return null;
}

/**
 * 구조편집 컨테이너(spTree/grpSp)를 모델 도형 리스트에 맞춰 **보수적으로** 재구성한다.
 * 그룹은 재귀한다(그룹 내부 add/delete/reorder 반영).
 *
 * 핵심: 모델은 **완전집합이 아니다** — 파서가 미지원 요소(SmartArt/OLE graphicFrame·
 * xfrm없는 sp/pic·빈 그룹·mc:AlternateContent)를 드롭하므로, "모델에 없음"으로 삭제를
 * 판정하면 그 요소들이 통째로 사라진다(데이터 유실). 따라서:
 *  - 삭제는 오직 deletedSet(편집기가 명시 기록한 cNvId)으로만 판정.
 *  - 매칭 안 됐고 삭제도 아닌 원본 요소(미모델/드롭)는 **원위치 보존**(직전 매칭 요소
 *    뒤에 weave — 상대 z-order 유지).
 *  - 매칭 요소는 모델 순서로, synthetic 은 합성, duplicatedFrom 은 clone.
 * head(nvGrpSpPr/grpSpPr)·tail(extLst) 보존.
 */
function reconcileShapeTree(
  xmlDoc: Document,
  container: Element,
  shapes: PptxShape[],
  deletedSet: Set<number>,
  globalIdMap?: Map<number, Element>,
): void {
  const anchor = findShapeInsertAnchor(container);
  const tailEl = findTailElement(container, anchor);

  // 1. body 요소(head 뒤 ~ tail 앞) 를 문서순으로 수집. 매칭 대상(SHAPE_ELEMENTS)만
  //    idMap/byType 에, 그 외(mc:AlternateContent 등)는 bodyEls 에만(미모델 → 보존).
  const bodyEls: Element[] = [];
  const idMap = new Map<number, Element>();
  const conflicted = new Set<number>();
  const byType = new Map<string, Element[]>();
  {
    let n: Node | null = anchor ? anchor.nextSibling : container.firstChild;
    while (n && n !== tailEl) {
      if (n.nodeType === 1) {
        const el = n as Element;
        bodyEls.push(el);
        if (el.namespaceURI === P_NS && SHAPE_ELEMENTS.has(el.localName)) {
          let list = byType.get(el.localName);
          if (!list) { list = []; byType.set(el.localName, list); }
          list.push(el);
          const id = elementCNvId(el);
          if (id !== null) {
            if (idMap.has(id)) conflicted.add(id);
            else idMap.set(id, el);
          }
        }
      }
      n = n.nextSibling;
    }
  }

  const consumed = new Set<Element>();
  const cursor = new Map<string, number>();
  const nextPositional = (ln: string): Element | null => {
    const list = byType.get(ln);
    if (!list) return null;
    let i = cursor.get(ln) ?? 0;
    while (i < list.length && (consumed.has(list[i]) || looksParserDropped(list[i]))) i++;
    cursor.set(ln, i + 1);
    return i < list.length ? list[i] : null;
  };
  const recurse = (child: Element, children: PptxShape[]) =>
    reconcileShapeTree(xmlDoc, child, children, deletedSet, globalIdMap);
  // 합성 폴백 — 세션 합성 그룹(p:grpSp 셸)은 자식을 재귀로 채운다(파스 자식은
  // 전역 idMap 의 duplicatedFrom 클론 → 충실도 보존).
  const synthesize = (shape: PptxShape): Element => {
    const el = synthesizeShapeEl(xmlDoc, shape);
    if (shape.type === 'group' && shape.children && shape.children.length > 0) {
      recurse(el, shape.children);
    }
    return el;
  };

  // 2. 모델 도형 → 요소. origEl(재사용된 원본)이 있으면 weave 앵커로 쓴다.
  const modelEls: Array<{ el: Element; origEl: Element | null }> = [];
  for (const shape of shapes) {
    let el: Element | null = null;
    let origEl: Element | null = null;

    if (shape.synthetic) {
      el = synthesize(shape);
    } else if (shape.duplicatedFrom !== undefined && shape.duplicatedFrom !== null) {
      // 컨테이너 스코프 우선, 미스면 전역 폴백(그룹화/해제 — 원본이 타 컨테이너).
      const local = !conflicted.has(shape.duplicatedFrom) ? idMap.get(shape.duplicatedFrom) : undefined;
      const src = local ?? globalIdMap?.get(shape.duplicatedFrom);
      if (src) {
        el = cloneShapeEl(xmlDoc, src, shape);
        // 복제본: 새 위치(transformDirty) + 텍스트/서식 편집분 반영.
        dispatchMatchedShape(xmlDoc, el, shape, recurse);
      } else {
        el = synthesize(shape);
      }
    } else {
      // 기존 도형: cNvId 앵커 → 형별 positional 폴백 (patchShapeTree 와 동일 규칙)
      if (shape.cNvId !== null && shape.cNvId !== undefined && !conflicted.has(shape.cNvId)) {
        const cand = idMap.get(shape.cNvId);
        if (cand && !consumed.has(cand)) el = cand;
      }
      if (!el) el = nextPositional(xmlLocalNameForShape(shape));
      if (el) {
        consumed.add(el);
        origEl = el;
        dispatchMatchedShape(xmlDoc, el, shape, recurse);
      }
    }
    if (el) modelEls.push({ el, origEl });
  }

  // 3. 미소비 body 요소를 삭제(deletedSet) vs 보존(미모델/드롭)으로 분리.
  //    보존 요소는 직전에 소비된 원본 요소(anchorEl)에 붙여 상대 위치를 유지한다.
  const preserved: Array<{ el: Element; anchorEl: Element | null }> = [];
  let lastConsumed: Element | null = null;
  for (const el of bodyEls) {
    if (consumed.has(el)) { lastConsumed = el; continue; }
    const id = (el.namespaceURI === P_NS && SHAPE_ELEMENTS.has(el.localName)) ? elementCNvId(el) : null;
    if (id !== null && deletedSet.has(id)) continue; // 명시 삭제 → 드롭
    preserved.push({ el, anchorEl: lastConsumed }); // 미모델/드롭 → 보존
  }

  // 4. 최종 순서 = [앵커없는 보존] + 각 모델요소 뒤에 [그 origEl 을 앵커로 한 보존]
  const finalOrder: Element[] = [];
  for (const p of preserved) if (p.anchorEl === null) finalOrder.push(p.el);
  for (const { el, origEl } of modelEls) {
    finalOrder.push(el);
    if (origEl) {
      for (const p of preserved) if (p.anchorEl === origEl) finalOrder.push(p.el);
    }
  }

  // 5. body 영역(head 뒤 ~ tail 앞) 노드 전부 제거 후 finalOrder 를 그 자리에 재삽입.
  //    (공백 텍스트노드도 함께 제거해 클러스터링 방지.)
  {
    let n: Node | null = anchor ? anchor.nextSibling : container.firstChild;
    while (n && n !== tailEl) {
      const next = n.nextSibling;
      container.removeChild(n);
      n = next;
    }
  }
  for (const el of finalOrder) {
    container.insertBefore(el, tailEl);
  }
}

/** 유효색이면 a:solidFill > a:srgbClr(+alpha) 요소, 무효(named/transparent)면 null. */
function makeSolidFillFromColor(xmlDoc: Document, color?: string): Element | null {
  const sf = xmlDoc.createElementNS(A_NS, 'a:solidFill');
  return setSolidFillColor(xmlDoc, sf, color) ? sf : null;
}

/** synthetic 합성 공용 — a:xfrm(rot/flip/off/ext, px→EMU). */
function buildXfrmEl(xmlDoc: Document, shape: PptxShape): Element {
  const xfrm = xmlDoc.createElementNS(A_NS, 'a:xfrm');
  if (shape.rotation) xfrm.setAttribute('rot', String(Math.round(shape.rotation * 60000)));
  if (shape.flipH) xfrm.setAttribute('flipH', '1');
  if (shape.flipV) xfrm.setAttribute('flipV', '1');
  const off = xmlDoc.createElementNS(A_NS, 'a:off');
  off.setAttribute('x', String(pxToEmu(shape.position.x)));
  off.setAttribute('y', String(pxToEmu(shape.position.y)));
  const ext = xmlDoc.createElementNS(A_NS, 'a:ext');
  ext.setAttribute('cx', String(pxToEmu(shape.size.width)));
  ext.setAttribute('cy', String(pxToEmu(shape.size.height)));
  xfrm.appendChild(off);
  xfrm.appendChild(ext);
  return xfrm;
}

/** synthetic 합성 공용 — a:prstGeom(모델 preset, custom/부재→폴백) + avLst(조정값). */
function buildPrstGeomEl(xmlDoc: Document, shape: PptxShape, fallback: string): Element {
  const p = shape.geometry?.preset;
  const geom = xmlDoc.createElementNS(A_NS, 'a:prstGeom');
  geom.setAttribute('prst', !p || p === 'custom' ? fallback : p);
  const avLst = xmlDoc.createElementNS(A_NS, 'a:avLst');
  for (const [name, val] of Object.entries(shape.geometry?.adjustments ?? {})) {
    const gd = xmlDoc.createElementNS(A_NS, 'a:gd');
    gd.setAttribute('name', name);
    gd.setAttribute('fmla', `val ${Math.round(val)}`);
    avLst.appendChild(gd);
  }
  geom.appendChild(avLst);
  return geom;
}

/** synthetic 합성 공용 — a:ln(w·solidFill·headEnd/tailEnd). 쓸 내용 없으면 null.
 *  w 단위 배수는 patchOutline 과 동일 관례(×12700). */
function buildOutlineEl(xmlDoc: Document, outline?: PptxOutline): Element | null {
  if (!outline) return null;
  const outlineFill = makeSolidFillFromColor(xmlDoc, outline.color);
  // 유효 색·폭·끝장식 중 하나는 있어야 ln 을 만든다(무효 색만이면 생략→상속).
  if (!outlineFill && outline.width === undefined && !outline.headEnd && !outline.tailEnd) return null;
  const ln = xmlDoc.createElementNS(A_NS, 'a:ln');
  if (outline.width !== undefined) {
    ln.setAttribute('w', String(Math.round(outline.width * 12700)));
  }
  if (outlineFill) ln.appendChild(outlineFill);
  // CT_LineProperties 순서상 headEnd/tailEnd 는 fill 뒤.
  const ends: Array<['a:headEnd' | 'a:tailEnd', PptxLineEnd | undefined]> = [
    ['a:headEnd', outline.headEnd],
    ['a:tailEnd', outline.tailEnd],
  ];
  for (const [name, end] of ends) {
    if (!end) continue;
    const el = xmlDoc.createElementNS(A_NS, name);
    el.setAttribute('type', end.type);
    if (end.w) el.setAttribute('w', end.w);
    if (end.len) el.setAttribute('len', end.len);
    ln.appendChild(el);
  }
  return ln;
}

/** 새 이미지(synthetic)용 p:pic — nvPicPr/blipFill(a:blip r:embed)/spPr(xfrm·rect).
 *  r:embed 의 rId 는 registerNewImages 가 슬라이드 _rels 에 등록한 값과 동일. */
function synthesizePicEl(xmlDoc: Document, shape: PptxShape): Element {
  const pic = xmlDoc.createElementNS(P_NS, 'p:pic');

  const nv = xmlDoc.createElementNS(P_NS, 'p:nvPicPr');
  const cNvPr = xmlDoc.createElementNS(P_NS, 'p:cNvPr');
  cNvPr.setAttribute('id', String(shape.cNvId ?? 0));
  cNvPr.setAttribute('name', shape.name || 'Picture');
  nv.appendChild(cNvPr);
  const cNvPicPr = xmlDoc.createElementNS(P_NS, 'p:cNvPicPr');
  const locks = xmlDoc.createElementNS(A_NS, 'a:picLocks');
  locks.setAttribute('noChangeAspect', '1');
  cNvPicPr.appendChild(locks);
  nv.appendChild(cNvPicPr);
  nv.appendChild(xmlDoc.createElementNS(P_NS, 'p:nvPr'));
  pic.appendChild(nv);

  const blipFill = xmlDoc.createElementNS(P_NS, 'p:blipFill');
  const blip = xmlDoc.createElementNS(A_NS, 'a:blip');
  if (shape.imageRId) blip.setAttributeNS(R_NS, 'r:embed', shape.imageRId);
  blipFill.appendChild(blip);
  const stretch = xmlDoc.createElementNS(A_NS, 'a:stretch');
  stretch.appendChild(xmlDoc.createElementNS(A_NS, 'a:fillRect'));
  blipFill.appendChild(stretch);
  pic.appendChild(blipFill);

  const spPr = xmlDoc.createElementNS(P_NS, 'p:spPr');
  spPr.appendChild(buildXfrmEl(xmlDoc, shape));
  const geom = xmlDoc.createElementNS(A_NS, 'a:prstGeom');
  geom.setAttribute('prst', 'rect');
  geom.appendChild(xmlDoc.createElementNS(A_NS, 'a:avLst'));
  spPr.appendChild(geom);
  pic.appendChild(spPr);
  patchPptxImageProperties(xmlDoc, pic, shape, true);
  return pic;
}

/** 새 도형(synthetic)용 최소 p:sp — nvSpPr/spPr(xfrm·prstGeom(모델 preset+avLst)·fill·ln)/
 *  txBody. type 'connector' 는 p:cxnSp 로, 'image' 는 p:pic 로 위임(스키마 상이). */
const TABLE_GRAPHIC_URI = 'http://schemas.openxmlformats.org/drawingml/2006/table';

type PptxTableGridT = NonNullable<PptxShape['tableGrid']>;
type PptxTableCellT = PptxTableGridT['rows'][number]['cells'][number];

/** 셀 테두리 한 변 → a:lnL/lnR/lnT/lnB 합성. */
function buildCellBorderEdge(
  xmlDoc: Document,
  name: string,
  border?: { width?: number; color?: string },
): Element | null {
  if (!border) return null;
  const ln = xmlDoc.createElementNS(A_NS, `a:${name}`);
  ln.setAttribute('w', String(Math.round((border.width ?? 1) * 12700)));
  const fill = xmlDoc.createElementNS(A_NS, 'a:solidFill');
  const clr = xmlDoc.createElementNS(A_NS, 'a:srgbClr');
  clr.setAttribute('val', (border.color ?? '#000000').replace('#', '').toUpperCase());
  fill.appendChild(clr);
  ln.appendChild(fill);
  return ln;
}

const CELL_BORDER_EDGES = ['lnL', 'lnR', 'lnT', 'lnB'] as const;
const CELL_FILL_MEMBERS = ['solidFill', 'gradFill', 'noFill', 'blipFill', 'pattFill', 'grpFill'];

/** 모델 셀 → a:tc 합성 — 표 신규 합성(synthesizeTableEl)과 구조 동기화
 *  (syncTableStructure)의 신규 셀 경로가 공유한다. */
function buildTableCellEl(xmlDoc: Document, cell: PptxTableCellT): Element {
  const tc = xmlDoc.createElementNS(A_NS, 'a:tc');
  if (cell.colSpan) tc.setAttribute('gridSpan', String(cell.colSpan));
  if (cell.rowSpan) tc.setAttribute('rowSpan', String(cell.rowSpan));
  if (cell.hMerge) tc.setAttribute('hMerge', '1');
  if (cell.vMerge) tc.setAttribute('vMerge', '1');
  tc.appendChild(buildTxBody(xmlDoc, cell.textBody, 'a'));
  // tcPr 자식 순서(CT_TableCellProperties): lnL → lnR → lnT → lnB → fill
  const tcPr = xmlDoc.createElementNS(A_NS, 'a:tcPr');
  for (const [name, b] of [
    ['lnL', cell.borders?.left], ['lnR', cell.borders?.right],
    ['lnT', cell.borders?.top], ['lnB', cell.borders?.bottom],
  ] as const) {
    const ln = buildCellBorderEdge(xmlDoc, name, b);
    if (ln) tcPr.appendChild(ln);
  }
  if (cell.fill?.type === 'solid' && cell.fill.color) {
    const fill = xmlDoc.createElementNS(A_NS, 'a:solidFill');
    const clr = xmlDoc.createElementNS(A_NS, 'a:srgbClr');
    clr.setAttribute('val', cell.fill.color.replace('#', '').slice(0, 6).toUpperCase());
    fill.appendChild(clr);
    tcPr.appendChild(fill);
  }
  tc.appendChild(tcPr);
  return tc;
}

/** 셀 서식(T4) — 편집기가 바꾼 셀에만(fillDirty/bordersDirty) tcPr 을 되쓴다.
 *  모델 값이 없으면(fill/borders 삭제 = 상속 복귀) 기존 요소 제거만 수행. */
function patchTableCellFormat(xmlDoc: Document, tcEl: Element, cell: PptxTableCellT): void {
  if (!cell.fillDirty && !cell.bordersDirty) return;
  let tcPr = directChildNS(tcEl, A_NS, 'tcPr');
  if (!tcPr) {
    tcPr = xmlDoc.createElementNS(A_NS, 'a:tcPr');
    tcEl.appendChild(tcPr); // tc 자식 순서: txBody → tcPr → extLst
  }

  if (cell.bordersDirty) {
    for (const name of CELL_BORDER_EDGES) {
      const el = directChildNS(tcPr, A_NS, name);
      if (el) tcPr.removeChild(el);
    }
    // 스키마상 ln* 는 tcPr 선두 그룹 — 첫 자식 앞에 역순 삽입해 lnL→lnR→lnT→lnB 유지.
    const edges = [
      ['lnL', cell.borders?.left], ['lnR', cell.borders?.right],
      ['lnT', cell.borders?.top], ['lnB', cell.borders?.bottom],
    ] as const;
    let anchor = tcPr.firstChild;
    for (const [name, b] of edges) {
      const ln = buildCellBorderEdge(xmlDoc, name, b);
      if (ln) tcPr.insertBefore(ln, anchor);
    }
    void anchor;
  }

  if (cell.fillDirty) {
    for (const name of CELL_FILL_MEMBERS) {
      const el = directChildNS(tcPr, A_NS, name);
      if (el) tcPr.removeChild(el);
    }
    if (cell.fill?.type === 'solid' && cell.fill.color) {
      const fill = xmlDoc.createElementNS(A_NS, 'a:solidFill');
      const clr = xmlDoc.createElementNS(A_NS, 'a:srgbClr');
      clr.setAttribute('val', cell.fill.color.replace('#', '').slice(0, 6).toUpperCase());
      fill.appendChild(clr);
      // fill 은 ln*/cell3D 뒤, headers/extLst 앞.
      const before = directChildNS(tcPr, A_NS, 'headers') ?? directChildNS(tcPr, A_NS, 'extLst');
      tcPr.insertBefore(fill, before);
    }
    // cell.fill 없음(=상속 복귀) → 제거만으로 완료.
  }
}

/** synthetic 표 합성 — p:graphicFrame(nvGraphicFramePr + **p:xfrm** + a:graphic>
 *  a:graphicData(table)>a:tbl). 셀 테두리는 모델 borders 를 tcPr a:lnL/R/T/B 로
 *  명시 기록 — 표 스타일 GUID 는 앱 내장이라 우리 렌더러/LO 가 해석 못 하는
 *  덱이 많고, 명시 테두리는 전 렌더러에서 동일 외형을 보장한다. */
function synthesizeTableEl(xmlDoc: Document, shape: PptxShape): Element {
  const grid = shape.tableGrid!;
  const frame = xmlDoc.createElementNS(P_NS, 'p:graphicFrame');

  const nvFramePr = xmlDoc.createElementNS(P_NS, 'p:nvGraphicFramePr');
  const cNvPr = xmlDoc.createElementNS(P_NS, 'p:cNvPr');
  cNvPr.setAttribute('id', String(shape.cNvId ?? 0));
  cNvPr.setAttribute('name', shape.name || 'Table');
  nvFramePr.appendChild(cNvPr);
  const cNvFramePr = xmlDoc.createElementNS(P_NS, 'p:cNvGraphicFramePr');
  const locks = xmlDoc.createElementNS(A_NS, 'a:graphicFrameLocks');
  locks.setAttribute('noGrp', '1');
  cNvFramePr.appendChild(locks);
  nvFramePr.appendChild(cNvFramePr);
  nvFramePr.appendChild(xmlDoc.createElementNS(P_NS, 'p:nvPr'));
  frame.appendChild(nvFramePr);

  // graphicFrame 의 변환은 spPr 이 아니라 p:xfrm 직계(자식 a:off/a:ext).
  const xfrm = xmlDoc.createElementNS(P_NS, 'p:xfrm');
  const off = xmlDoc.createElementNS(A_NS, 'a:off');
  off.setAttribute('x', String(pxToEmu(shape.position.x)));
  off.setAttribute('y', String(pxToEmu(shape.position.y)));
  const ext = xmlDoc.createElementNS(A_NS, 'a:ext');
  ext.setAttribute('cx', String(pxToEmu(shape.size.width)));
  ext.setAttribute('cy', String(pxToEmu(shape.size.height)));
  xfrm.appendChild(off);
  xfrm.appendChild(ext);
  frame.appendChild(xfrm);

  const graphic = xmlDoc.createElementNS(A_NS, 'a:graphic');
  const graphicData = xmlDoc.createElementNS(A_NS, 'a:graphicData');
  graphicData.setAttribute('uri', TABLE_GRAPHIC_URI);
  const tbl = xmlDoc.createElementNS(A_NS, 'a:tbl');
  tbl.appendChild(xmlDoc.createElementNS(A_NS, 'a:tblPr'));

  const tblGrid = xmlDoc.createElementNS(A_NS, 'a:tblGrid');
  for (const w of grid.gridCols) {
    const gridCol = xmlDoc.createElementNS(A_NS, 'a:gridCol');
    gridCol.setAttribute('w', String(pxToEmu(w)));
    tblGrid.appendChild(gridCol);
  }
  tbl.appendChild(tblGrid);

  for (const row of grid.rows) {
    const tr = xmlDoc.createElementNS(A_NS, 'a:tr');
    tr.setAttribute('h', String(pxToEmu(row.height)));
    for (const cell of row.cells) {
      tr.appendChild(buildTableCellEl(xmlDoc, cell));
    }
    tbl.appendChild(tr);
  }

  graphicData.appendChild(tbl);
  graphic.appendChild(graphicData);
  frame.appendChild(graphic);
  return frame;
}

function synthesizeShapeEl(xmlDoc: Document, shape: PptxShape): Element {
  if (shape.type === 'connector') return synthesizeConnectorEl(xmlDoc, shape);
  if (shape.type === 'image') return synthesizePicEl(xmlDoc, shape);
  if (shape.type === 'table' && shape.tableGrid) return synthesizeTableEl(xmlDoc, shape);
  if (shape.type === 'group') return synthesizeGroupEl(xmlDoc, shape);
  const sp = xmlDoc.createElementNS(P_NS, 'p:sp');

  const nvSpPr = xmlDoc.createElementNS(P_NS, 'p:nvSpPr');
  const cNvPr = xmlDoc.createElementNS(P_NS, 'p:cNvPr');
  cNvPr.setAttribute('id', String(shape.cNvId ?? 0));
  cNvPr.setAttribute('name', shape.name || 'Shape');
  nvSpPr.appendChild(cNvPr);
  const cNvSpPr = xmlDoc.createElementNS(P_NS, 'p:cNvSpPr');
  if (shape.type === 'textbox') cNvSpPr.setAttribute('txBox', '1');
  nvSpPr.appendChild(cNvSpPr);
  const nvPr = xmlDoc.createElementNS(P_NS, 'p:nvPr');
  // placeholder(새 슬라이드의 제목/본문 상자 등) — p:ph 로 기록해야 PowerPoint 가
  // 레이아웃 상속(프롬프트 텍스트·리스트 스타일)을 적용한다.
  if (shape.placeholder) {
    const ph = xmlDoc.createElementNS(P_NS, 'p:ph');
    if (shape.placeholder.type) ph.setAttribute('type', shape.placeholder.type);
    if (shape.placeholder.idx !== undefined) ph.setAttribute('idx', String(shape.placeholder.idx));
    nvPr.appendChild(ph);
  }
  nvSpPr.appendChild(nvPr);
  sp.appendChild(nvSpPr);

  const spPr = xmlDoc.createElementNS(P_NS, 'p:spPr');
  spPr.appendChild(buildXfrmEl(xmlDoc, shape));
  spPr.appendChild(buildPrstGeomEl(xmlDoc, shape, 'rect'));

  const fillEl = shape.fill && shape.fill.type === 'solid'
    ? makeSolidFillFromColor(xmlDoc, shape.fill.color) : null;
  if (fillEl) {
    spPr.appendChild(fillEl);
  } else if (shape.type === 'textbox' || shape.fill?.type === 'none') {
    // 명시 무채움(a:noFill) 보존 — 생략하면 테마 기본 채움으로 되살아난다
    // (noFill 텍스트박스 잘라내기→교차 붙여넣기 재합성 경로).
    spPr.appendChild(xmlDoc.createElementNS(A_NS, 'a:noFill'));
  }

  const ln = buildOutlineEl(xmlDoc, shape.outline);
  if (ln) spPr.appendChild(ln);
  sp.appendChild(spPr);

  sp.appendChild(buildTxBody(xmlDoc, shape.textBody));
  if (shape.formatPainterDirty) patchShapeElement(xmlDoc, sp, shape);
  return sp;
}

/** 새 그룹(synthetic)용 p:grpSp **셸** — nvGrpSpPr(cNvPr·cNvGrpSpPr·nvPr) +
 *  grpSpPr(a:xfrm off/ext/chOff/chExt). 자식 요소는 reconcileShapeTree 재귀가
 *  이 컨테이너 안에 채운다(파스 자식 = 전역 idMap duplicatedFrom 클론). */
function synthesizeGroupEl(xmlDoc: Document, shape: PptxShape): Element {
  const grpSp = xmlDoc.createElementNS(P_NS, 'p:grpSp');

  const nv = xmlDoc.createElementNS(P_NS, 'p:nvGrpSpPr');
  const cNvPr = xmlDoc.createElementNS(P_NS, 'p:cNvPr');
  cNvPr.setAttribute('id', String(shape.cNvId ?? 0));
  cNvPr.setAttribute('name', shape.name || 'Group');
  nv.appendChild(cNvPr);
  nv.appendChild(xmlDoc.createElementNS(P_NS, 'p:cNvGrpSpPr'));
  nv.appendChild(xmlDoc.createElementNS(P_NS, 'p:nvPr'));
  grpSp.appendChild(nv);

  const grpSpPr = xmlDoc.createElementNS(P_NS, 'p:grpSpPr');
  const xfrm = buildXfrmEl(xmlDoc, shape);
  writeGroupChildFrame(xmlDoc, xfrm, shape); // chOff/chExt(off→ext 뒤 순서)
  grpSpPr.appendChild(xfrm);
  grpSp.appendChild(grpSpPr);
  return grpSp;
}

/** 새 커넥터(synthetic)용 p:cxnSp — nvCxnSpPr(cNvPr·cNvCxnSpPr(stCxn/endCxn 글루)·nvPr)/
 *  spPr(xfrm·prstGeom·ln(화살촉)). txBody 는 스키마상 없다. 합성이 모델 현재값(글루·
 *  조정값·재경로 preset)을 그대로 읽으므로 삽입 후 편집분도 저장 시 반영된다. */
function synthesizeConnectorEl(xmlDoc: Document, shape: PptxShape): Element {
  const cxnSp = xmlDoc.createElementNS(P_NS, 'p:cxnSp');

  const nv = xmlDoc.createElementNS(P_NS, 'p:nvCxnSpPr');
  const cNvPr = xmlDoc.createElementNS(P_NS, 'p:cNvPr');
  cNvPr.setAttribute('id', String(shape.cNvId ?? 0));
  cNvPr.setAttribute('name', shape.name || 'Connector');
  nv.appendChild(cNvPr);
  const cNvCxnSpPr = xmlDoc.createElementNS(P_NS, 'p:cNvCxnSpPr');
  const conns: Array<['a:stCxn' | 'a:endCxn', PptxShape['startConnection']]> = [
    ['a:stCxn', shape.startConnection],
    ['a:endCxn', shape.endConnection],
  ];
  for (const [name, conn] of conns) {
    if (!conn) continue;
    const el = xmlDoc.createElementNS(A_NS, name);
    el.setAttribute('id', String(conn.targetCNvId));
    el.setAttribute('idx', String(conn.idx));
    cNvCxnSpPr.appendChild(el);
  }
  nv.appendChild(cNvCxnSpPr);
  nv.appendChild(xmlDoc.createElementNS(P_NS, 'p:nvPr'));
  cxnSp.appendChild(nv);

  const spPr = xmlDoc.createElementNS(P_NS, 'p:spPr');
  spPr.appendChild(buildXfrmEl(xmlDoc, shape));
  // custom(재경로 폴리라인)은 cxnSp 에 금지(PowerPoint 복구 유발) — 직선으로 폴백.
  spPr.appendChild(buildPrstGeomEl(xmlDoc, shape, 'line'));
  const ln = buildOutlineEl(xmlDoc, shape.outline);
  if (ln) spPr.appendChild(ln);
  cxnSp.appendChild(spPr);
  return cxnSp;
}

/** 모델 textBody → a:txBody(bodyPr/lstStyle/p·r·t). 빈 문단은 endParaRPr. */
function buildTxBody(xmlDoc: Document, textBody?: PptxTextBody, ns: 'p' | 'a' = 'p'): Element {
  // 도형은 p:txBody, 표 셀(a:tc)은 a:txBody — 내용 구조는 동일.
  const txBody = ns === 'a'
    ? xmlDoc.createElementNS(A_NS, 'a:txBody')
    : xmlDoc.createElementNS(P_NS, 'p:txBody');
  txBody.appendChild(xmlDoc.createElementNS(A_NS, 'a:bodyPr'));
  txBody.appendChild(xmlDoc.createElementNS(A_NS, 'a:lstStyle'));
  const paras = textBody?.paragraphs ?? [];
  if (paras.length === 0) {
    txBody.appendChild(xmlDoc.createElementNS(A_NS, 'a:p'));
    return txBody;
  }
  for (const para of paras) {
    const p = xmlDoc.createElementNS(A_NS, 'a:p');
    const defs = para.defaultRunProps;
    const wantsDefRPr = defs && (defs.fontSize !== undefined || defs.bold !== undefined);
    // 줄간격/글머리는 편집기가 실제 바꾼 것만(dirty 게이트 — 상속값 베이킹 방지).
    const wantsLnSpc = para.spacingDirty && (para.spacing?.lineSpacing ?? 0) > 0;
    const wantsBullet = para.bulletDirty && !!para.bulletType;
    if (para.alignment || wantsDefRPr || wantsLnSpc || wantsBullet) {
      const pPr = xmlDoc.createElementNS(A_NS, 'a:pPr');
      if (para.alignment) pPr.setAttribute('algn', para.alignment);
      // CT_TextParagraphProperties 자식 순서: lnSpc → bullet → defRPr
      if (wantsLnSpc) patchLineSpacing(xmlDoc, pPr, para);
      if (wantsBullet) patchBullet(xmlDoc, pPr, para);
      // 문단 기본 런 서식(a:defRPr) — 합성 도형(placeholder 등)의 상속/보정 크기를
      // 파일에 명시해 PowerPoint·재파싱과 편집기 표시가 일치하게 한다.
      if (wantsDefRPr) {
        const defRPr = xmlDoc.createElementNS(A_NS, 'a:defRPr');
        if (defs.fontSize !== undefined) defRPr.setAttribute('sz', String(Math.round(defs.fontSize * 100)));
        if (defs.bold) defRPr.setAttribute('b', '1');
        pPr.appendChild(defRPr);
      }
      p.appendChild(pPr);
    }
    let hasRun = false;
    for (const run of para.runs) {
      if (run.type === 'break') {
        p.appendChild(xmlDoc.createElementNS(A_NS, 'a:br'));
        continue;
      }
      const r = xmlDoc.createElementNS(A_NS, 'a:r');
      const rPr = buildRunRPr(xmlDoc, run);
      if (rPr) r.appendChild(rPr);
      const t = xmlDoc.createElementNS(A_NS, 'a:t');
      t.textContent = run.text ?? '';
      r.appendChild(t);
      p.appendChild(r);
      hasRun = true;
    }
    if (!hasRun) p.appendChild(xmlDoc.createElementNS(A_NS, 'a:endParaRPr'));
    txBody.appendChild(p);
  }
  return txBody;
}

/** 런 서식(b/i/u/strike/sz/color/latin) 이 있으면 a:rPr, 없으면 null. */
function buildRunRPr(xmlDoc: Document, run: PptxRun): Element | null {
  const hasAny =
    run.bold !== undefined || run.italic !== undefined || run.underline !== undefined ||
    run.strike !== undefined || run.fontSize !== undefined || !!run.color || !!run.fontFamily;
  if (!hasAny) return null;
  const rPr = xmlDoc.createElementNS(A_NS, 'a:rPr');
  if (run.bold !== undefined) rPr.setAttribute('b', run.bold ? '1' : '0');
  if (run.italic !== undefined) rPr.setAttribute('i', run.italic ? '1' : '0');
  if (run.underline !== undefined) rPr.setAttribute('u', run.underline ? 'sng' : 'none');
  if (run.strike !== undefined) rPr.setAttribute('strike', run.strike ? 'sngStrike' : 'noStrike');
  if (run.fontSize !== undefined) rPr.setAttribute('sz', String(Math.round(run.fontSize * 100)));
  const colorFill = run.color ? makeSolidFillFromColor(xmlDoc, run.color) : null;
  if (colorFill) rPr.appendChild(colorFill);
  if (run.fontFamily) {
    const latin = xmlDoc.createElementNS(A_NS, 'a:latin');
    latin.setAttribute('typeface', run.fontFamily);
    rPr.appendChild(latin);
  }
  return rPr;
}

/** 원본 도형 요소를 deep clone 하고 자기 cNvPr@id/name 만 새 값으로. (그룹/표는
 *  hook 에서 복제 차단 — 중첩 cNvPr 재할당 불필요.) 위치/텍스트/서식은 호출측이
 *  patchShapeElement 로 반영한다(복제본은 transformDirty=true). */
function cloneShapeEl(xmlDoc: Document, src: Element, shape: PptxShape): Element {
  const clone = src.cloneNode(true) as Element;
  const cNvPr = clone.getElementsByTagNameNS(P_NS, 'cNvPr')[0];
  if (cNvPr) {
    if (shape.cNvId !== null && shape.cNvId !== undefined) cNvPr.setAttribute('id', String(shape.cNvId));
    if (shape.name) cNvPr.setAttribute('name', shape.name);
  }
  return clone;
}

/* ------------------------------------------------------------------ */
/* 개별 도형 패치                                                       */
/* ------------------------------------------------------------------ */

function patchShapeElement(xmlDoc: Document, spEl: Element, shape: PptxShape): void {
  if (shape.textBody) {
    patchTextBody(xmlDoc, spEl, shape.textBody);
  }

  if (shape.type === 'table' && shape.tableGrid) {
    patchTableCells(xmlDoc, spEl, shape.tableGrid);
  }

  // xfrm(위치/크기/회전)은 편집기가 실제로 변형한 도형(transformDirty)에만 쓴다.
  // dirty(fill/outline)와 독립 게이트 — 무변형 도형의 xfrm 상속을 보존한다.
  // patchTransform 이 타입별 위치(sp→spPr, pic→spPr, graphicFrame→p:xfrm)를 처리한다.
  if (shape.transformDirty) {
    patchTransform(xmlDoc, spEl, shape);
  }

  // 커넥터 직교 재경로: prstGeom@prst 를 매핑된 bentConnectorN 으로 갱신(+옛 avLst 비움).
  // custGeom 이 아니라 프리셋으로 유지해야 PowerPoint 가 복구 없이 연다. patchAdjustments 가
  // 뒤이어 새 프리셋의 adj gd 를 채운다. (geometryDirty=재경로된 도형만.)
  if (shape.geometryDirty && shape.geometry?.preset && shape.geometry.preset !== 'custom') {
    patchConnectorPreset(xmlDoc, spEl, shape.geometry.preset);
  }

  // prstGeom/avLst 조정값(커넥터 꺾임 등)은 편집기가 실제로 바꾼 도형에만 되쓴다.
  if (shape.adjustmentsDirty && shape.geometry?.adjustments) {
    patchAdjustments(xmlDoc, spEl, shape.geometry.adjustments);
  }

  // 커넥터 글루(stCxn/endCxn)는 편집기가 실제로 (재)연결/해제한 커넥터에만 되쓴다.
  if (shape.connectionsDirty) {
    patchConnections(xmlDoc, spEl, shape);
  }

  // 직교 재경로: prstGeom → custGeom 교체(커넥터 꺾은선). transformDirty 가 xfrm
  // (rot/flip 리셋)을 이미 처리하므로 여기선 지오메트리만 바꾼다.
  if (shape.geometryDirty && shape.geometry?.preset === 'custom' && shape.geometry.custom?.length) {
    patchCustomGeometry(xmlDoc, spEl, shape.geometry.custom);
  }

  // fill/outline 은 편집기가 실제로 변경한 도형(shape.dirty)에만 쓴다.
  // 모델의 fill/outline 은 테마/스타일에서 "해석된" 값이라, 무조건 되쓰면
  // 원본에 없던 명시 채움이 구워져 테마 연결이 끊긴다(의미 변경).
  if (!shape.dirty) return;

  const spPr = directChildNS(spEl, P_NS, 'spPr');
  if (spPr) {
    if (shape.formatPainterDirty) {
      patchPaintedShape(xmlDoc, spPr, shape, setSolidFillColor);
      return;
    }
    if (shape.fill) {
      patchFill(xmlDoc, spPr, shape.fill);
    }
    if (shape.outline) {
      patchOutline(xmlDoc, spPr, shape.outline);
    }
  }
}

/** 커넥터 글루(a:stCxn/a:endCxn)를 p:cNvCxnSpPr 에 upsert/삭제.
 *  cNvCxnSpPr 자식 순서: cxnSpLocks?, stCxn?, endCxn?, extLst?.
 *  startConnection/endConnection 부재면 기존 요소를 **삭제**(글루 해제 반영). */
function patchConnections(xmlDoc: Document, spEl: Element, shape: PptxShape): void {
  const nvCxnSpPr = directChildNS(spEl, P_NS, 'nvCxnSpPr');
  if (!nvCxnSpPr) return; // 커넥터(cxnSp)만 대상
  let cNv = directChildNS(nvCxnSpPr, P_NS, 'cNvCxnSpPr');
  // 글루가 아예 없고 기존 cNvCxnSpPr 도 없으면 빈 요소를 새로 만들 필요 없음.
  if (!cNv && !shape.startConnection && !shape.endConnection) return;
  if (!cNv) {
    // nvCxnSpPr 자식 순서: cNvPr, cNvCxnSpPr, nvPr → cNvPr 다음에 삽입.
    cNv = xmlDoc.createElementNS(P_NS, 'p:cNvCxnSpPr');
    const cNvPr = directChildNS(nvCxnSpPr, P_NS, 'cNvPr');
    if (cNvPr && cNvPr.nextSibling) nvCxnSpPr.insertBefore(cNv, cNvPr.nextSibling);
    else if (cNvPr) nvCxnSpPr.appendChild(cNv);
    else nvCxnSpPr.insertBefore(cNv, nvCxnSpPr.firstChild);
  }
  upsertCxn(xmlDoc, cNv, 'stCxn', shape.startConnection, ['endCxn', 'extLst']);
  upsertCxn(xmlDoc, cNv, 'endCxn', shape.endConnection, ['extLst']);
}

/** 커넥터 재경로: spPr>prstGeom 의 prst 를 매핑된 bentConnectorN 으로 바꾸고 avLst 를 비운다.
 *  (뒤이어 patchAdjustments 가 새 프리셋의 adj gd 를 기록.) custGeom 은 커넥터에 안 쓴다
 *  (PowerPoint 복구 회피 — patchCustomGeometry 가드 참고). prstGeom 부재면 무동작. */
function patchConnectorPreset(xmlDoc: Document, spEl: Element, preset: string): void {
  const spPr = directChildNS(spEl, P_NS, 'spPr');
  if (!spPr) return;
  const prstGeom = directChildNS(spPr, A_NS, 'prstGeom');
  if (!prstGeom) return; // 커넥터는 항상 prstGeom 보유(재경로 대상)
  prstGeom.setAttribute('prst', preset);
  // 옛 프리셋의 avLst gd 제거(스테일 조정값 방지) — 없으면 만들어 둠(patchAdjustments 재사용).
  let avLst = directChildNS(prstGeom, A_NS, 'avLst');
  if (!avLst) {
    avLst = xmlDoc.createElementNS(A_NS, 'a:avLst');
    prstGeom.insertBefore(avLst, prstGeom.firstChild);
  } else {
    while (avLst.firstChild) avLst.removeChild(avLst.firstChild);
  }
}

/** spPr 의 prstGeom/custGeom 을 새 custGeom(폴리라인)으로 교체(커넥터 직교 재경로).
 *  경로 좌표는 px → EMU. a:path fill="none"(선). spPr 자식순서: xfrm 다음. */
function patchCustomGeometry(
  xmlDoc: Document,
  spEl: Element,
  customPaths: Array<{ d: string; w: number; h: number }>,
): void {
  // ★ 커넥터(cxnSp)에는 custGeom 을 절대 쓰지 않는다. <p:cxnSp> 안의 <a:custGeom> 는
  //   ISO-29500 스키마상 합법이지만 PowerPoint 가 "복구" 다이얼로그로 거부한다(앱 계층
  //   제약: 커넥터는 prstGeom 커넥터 프리셋만 소비). 커넥터 꺾임 재경로는 bentConnectorN
  //   프리셋 + avLst 조정값으로 표현하고(patchAdjustments 경유), custGeom 은 freeform
  //   도형(p:sp)에만 쓴다. 이 가드가 최종 안전망 — 모델이 어떤 상태든 cxnSp+custGeom 미출하.
  if (directChildNS(spEl, P_NS, 'nvCxnSpPr')) return;
  const spPr = directChildNS(spEl, P_NS, 'spPr');
  if (!spPr) return;
  const cg = xmlDoc.createElementNS(A_NS, 'a:custGeom');
  cg.appendChild(xmlDoc.createElementNS(A_NS, 'a:avLst'));
  cg.appendChild(xmlDoc.createElementNS(A_NS, 'a:gdLst'));
  cg.appendChild(xmlDoc.createElementNS(A_NS, 'a:ahLst'));
  const rectEl = xmlDoc.createElementNS(A_NS, 'a:rect');
  for (const a of ['l', 't', 'r', 'b']) rectEl.setAttribute(a, '0');
  cg.appendChild(rectEl);
  const pathLst = xmlDoc.createElementNS(A_NS, 'a:pathLst');
  for (const p of customPaths) {
    const path = xmlDoc.createElementNS(A_NS, 'a:path');
    // 축정렬(수평/수직) 경로는 w 또는 h 가 0 — 일부 소비자가 degenerate 로 취급하니
    // 최소 1 EMU 보장(평면 경로라 좌표엔 영향 없음).
    path.setAttribute('w', String(Math.max(1, pxToEmu(p.w))));
    path.setAttribute('h', String(Math.max(1, pxToEmu(p.h))));
    path.setAttribute('fill', 'none');
    // d = "M x,y L x,y ..."(콤마/공백 구분자 모두 허용). 각 명령을 moveTo/lnTo>pt 로.
    for (const m of p.d.matchAll(/([ML])\s*(-?[\d.]+)[\s,]+(-?[\d.]+)/g)) {
      const seg = xmlDoc.createElementNS(A_NS, m[1] === 'M' ? 'a:moveTo' : 'a:lnTo');
      const pt = xmlDoc.createElementNS(A_NS, 'a:pt');
      pt.setAttribute('x', String(pxToEmu(parseFloat(m[2]))));
      pt.setAttribute('y', String(pxToEmu(parseFloat(m[3]))));
      seg.appendChild(pt);
      path.appendChild(seg);
    }
    pathLst.appendChild(path);
  }
  cg.appendChild(pathLst);
  const old = directChildNS(spPr, A_NS, 'prstGeom') ?? directChildNS(spPr, A_NS, 'custGeom');
  if (old) {
    spPr.replaceChild(cg, old);
  } else {
    const xfrm = directChildNS(spPr, A_NS, 'xfrm');
    if (xfrm && xfrm.nextSibling) spPr.insertBefore(cg, xfrm.nextSibling);
    else if (xfrm) spPr.appendChild(cg);
    else spPr.insertBefore(cg, spPr.firstChild);
  }
}

/** a:stCxn|a:endCxn upsert(conn 있으면 id/idx 세팅) 또는 삭제(conn 부재). */
function upsertCxn(
  xmlDoc: Document,
  cNv: Element,
  name: 'stCxn' | 'endCxn',
  conn: { targetCNvId: number; idx: number } | undefined,
  beforeLocalNames: string[],
): void {
  let el = directChildNS(cNv, A_NS, name);
  if (!conn) {
    if (el) cNv.removeChild(el); // 글루 해제 → stale 요소 제거
    return;
  }
  if (!el) {
    el = xmlDoc.createElementNS(A_NS, `a:${name}`);
    const before = firstDirectChildOfAny(cNv, beforeLocalNames);
    if (before) cNv.insertBefore(el, before); else cNv.appendChild(el);
  }
  el.setAttribute('id', String(conn.targetCNvId));
  el.setAttribute('idx', String(conn.idx));
}

/* ------------------------------------------------------------------ */
/* TextBody 패치                                                        */
/* ------------------------------------------------------------------ */

function patchTextBody(xmlDoc: Document, parentEl: Element, textBody: PptxTextBody): void {
  const txBodyEl = parentEl.getElementsByTagNameNS(P_NS, 'txBody')[0];
  if (!txBodyEl) return;
  patchTextBodyEl(xmlDoc, txBodyEl, textBody);
}

/** 표 셀 텍스트 되쓰기 — 모델 rows[r].cells[c] 는 파서가 XML tr/tc 와 1:1 로
 *  구축한다(hMerge/vMerge placeholder 셀 포함) → 인덱스 매칭이 안전하다.
 *  셀 txBody 는 a:txBody(A_NS) 라 도형의 p:txBody 경로(patchTextBody)와 다르다. */
/** 표 행/열 구조 동기화(T3) — srcIndex 로 원본 tr/tc 를 재사용(충실도 보존)하며
 *  모델 순서로 재조립한다. 신규 행/셀은 모델(스타일 계승 완료)로부터 합성,
 *  모델에서 사라진 원본은 제거. gridCol 은 w 뿐이라 전체 재작성. */
function syncTableStructure(xmlDoc: Document, tblEl: Element, grid: PptxTableGridT): void {
  const directEls = (parent: Element, name: string): Element[] =>
    Array.from(parent.childNodes).filter((n): n is Element =>
      n.nodeType === 1 && (n as Element).localName === name && (n as Element).namespaceURI === A_NS);

  const tblGrid = directChildNS(tblEl, A_NS, 'tblGrid');
  if (tblGrid) {
    while (tblGrid.firstChild) tblGrid.removeChild(tblGrid.firstChild);
    for (const w of grid.gridCols) {
      const gridCol = xmlDoc.createElementNS(A_NS, 'a:gridCol');
      gridCol.setAttribute('w', String(pxToEmu(w)));
      tblGrid.appendChild(gridCol);
    }
  }

  const origTrs = directEls(tblEl, 'tr');
  for (const tr of origTrs) tblEl.removeChild(tr);

  const setOrRemove = (el: Element, attr: string, value: string | null) => {
    if (value === null) el.removeAttribute(attr);
    else el.setAttribute(attr, value);
  };

  for (const row of grid.rows) {
    let trEl: Element;
    const origTr = row.srcIndex !== undefined ? origTrs[row.srcIndex] : undefined;
    if (origTr) {
      trEl = origTr;
      trEl.setAttribute('h', String(pxToEmu(row.height)));
      // 셀 재조립 — 원본 tc 는 srcIndex 로 재사용(속성만 모델과 동기화),
      // 신규 셀은 모델로부터 합성. 순서는 모델 기준.
      const origTcs = directEls(trEl, 'tc');
      for (const tc of origTcs) trEl.removeChild(tc);
      for (const cell of row.cells) {
        const origTc = cell.srcIndex !== undefined ? origTcs[cell.srcIndex] : undefined;
        if (origTc) {
          setOrRemove(origTc, 'gridSpan', cell.colSpan && cell.colSpan > 1 ? String(cell.colSpan) : null);
          setOrRemove(origTc, 'rowSpan', cell.rowSpan && cell.rowSpan > 1 ? String(cell.rowSpan) : null);
          setOrRemove(origTc, 'hMerge', cell.hMerge ? '1' : null);
          setOrRemove(origTc, 'vMerge', cell.vMerge ? '1' : null);
          trEl.appendChild(origTc);
        } else {
          trEl.appendChild(buildTableCellEl(xmlDoc, cell));
        }
      }
    } else {
      trEl = xmlDoc.createElementNS(A_NS, 'a:tr');
      trEl.setAttribute('h', String(pxToEmu(row.height)));
      for (const cell of row.cells) {
        trEl.appendChild(buildTableCellEl(xmlDoc, cell));
      }
    }
    tblEl.appendChild(trEl);
  }
}

function patchTableCells(xmlDoc: Document, frameEl: Element, grid: NonNullable<PptxShape['tableGrid']>): void {
  const tblEl = frameEl.getElementsByTagNameNS(A_NS, 'tbl')[0];
  if (!tblEl) return;
  if (grid.structureDirty) syncTableStructure(xmlDoc, tblEl, grid);
  const trEls = Array.from(tblEl.childNodes).filter((n): n is Element =>
    n.nodeType === 1 && (n as Element).localName === 'tr' && (n as Element).namespaceURI === A_NS);
  for (let r = 0; r < grid.rows.length && r < trEls.length; r++) {
    const tcEls = Array.from(trEls[r].childNodes).filter((n): n is Element =>
      n.nodeType === 1 && (n as Element).localName === 'tc' && (n as Element).namespaceURI === A_NS);
    const cells = grid.rows[r].cells;
    for (let c = 0; c < cells.length && c < tcEls.length; c++) {
      patchTableCellFormat(xmlDoc, tcEls[c], cells[c]);
      const textBody = cells[c].textBody;
      if (!textBody) continue;
      const txBodyEl = directChildNS(tcEls[c], A_NS, 'txBody');
      if (txBodyEl) patchTextBodyEl(xmlDoc, txBodyEl, textBody);
    }
  }
}

function patchTextBodyEl(xmlDoc: Document, txBodyEl: Element, textBody: PptxTextBody): void {
  if (textBody.bodyPropsDirty) patchPaintedBody(xmlDoc, txBodyEl, textBody);
  // 문단 구조 동기화 — 편집기가 문단 수를 바꾼 텍스트바디(textBody.structureDirty,
  // 셀 병합의 문단 이동 등)에서만. 모델 초과분은 빈 a:p 를 뒤에 추가(내용은 아래
  // patchParagraph 의 dirty 런 동기화가 채움), 모델보다 많은 a:p 는 제거.
  if (textBody.structureDirty) {
    const ps = Array.from(txBodyEl.getElementsByTagNameNS(A_NS, 'p'))
      .filter((el) => el.parentNode === txBodyEl);
    for (let i = textBody.paragraphs.length; i < ps.length; i++) {
      txBodyEl.removeChild(ps[i]);
    }
    for (let i = ps.length; i < textBody.paragraphs.length; i++) {
      txBodyEl.appendChild(xmlDoc.createElementNS(A_NS, 'a:p'));
    }
  }
  const existingParagraphs = Array.from(txBodyEl.getElementsByTagNameNS(A_NS, 'p'));

  for (let i = 0; i < textBody.paragraphs.length && i < existingParagraphs.length; i++) {
    patchParagraph(xmlDoc, existingParagraphs[i], textBody.paragraphs[i]);
  }
}

function patchParagraph(xmlDoc: Document, pEl: Element, para: PptxParagraph): void {
  // 문단 속성은 편집기가 실제로 바꾼 것만 쓴다(모델 값은 상속 해석값일 수 있음):
  // algn=para.dirty, 줄간격=spacingDirty, 글머리=bulletDirty. 필요 시 pPr 생성
  // (a:p 첫 자식 — 종전엔 pPr 없는 문단의 정렬 편집이 무음 유실됐다).
  let pPr = pEl.getElementsByTagNameNS(A_NS, 'pPr')[0];
  const needsPPr = (!!para.alignment && para.dirty) || para.spacingDirty || para.bulletDirty || para.formatPainterDirty;
  if (!pPr && needsPPr) {
    pPr = xmlDoc.createElementNS(A_NS, 'a:pPr');
    pEl.insertBefore(pPr, pEl.firstChild);
  }
  if (pPr && para.alignment && para.dirty) {
    pPr.setAttribute('algn', para.alignment);
  }
  if (pPr && para.formatPainterDirty) patchPaintedParagraph(xmlDoc, pPr, para, setSolidFillColor);
  else {
    if (pPr && para.spacingDirty) patchLineSpacing(xmlDoc, pPr, para);
    if (pPr && para.bulletDirty) patchBullet(xmlDoc, pPr, para);
  }

  const existingRuns: Element[] = [];
  for (let i = 0; i < pEl.childNodes.length; i++) {
    const child = pEl.childNodes[i];
    if (child.nodeType === 1 && (child as Element).localName === 'r' && (child as Element).namespaceURI === A_NS) {
      existingRuns.push(child as Element);
    }
  }

  const textRuns = para.runs.filter(r => r.type === 'text');

  for (let i = 0; i < textRuns.length && i < existingRuns.length; i++) {
    patchRun(xmlDoc, existingRuns[i], textRuns[i]);
  }

  // 런 구조 동기화 — 편집된 문단(dirty 런 존재)에서만. 무편집 문단은
  // 위 인덱스 갱신만으로 의미 중립(no-op)을 유지한다.
  //  ① 모델에만 있는 런 추가: 빈 placeholder 첫 타이핑은 원본 a:p 에
  //     a:r 이 0개라 종전 min-루프에서 통째로 유실됐다(실앱 QA 재현).
  //  ② 모델보다 많은 XML 런 제거: 단일런 폴백 커밋 뒤 잔존 런이
  //     옛 텍스트를 중복 노출하는 것을 막는다.
  if (textRuns.some(r => r.dirty)) {
    for (let i = existingRuns.length - 1; i >= textRuns.length; i--) {
      pEl.removeChild(existingRuns[i]);
    }
    const endParaRPr = directChildNS(pEl, A_NS, 'endParaRPr');
    for (let i = existingRuns.length; i < textRuns.length; i++) {
      const rEl = xmlDoc.createElementNS(A_NS, 'a:r');
      const tEl = xmlDoc.createElementNS(A_NS, 'a:t');
      tEl.textContent = textRuns[i].text ?? '';
      rEl.appendChild(tEl);
      pEl.insertBefore(rEl, endParaRPr);
      patchRun(xmlDoc, rEl, textRuns[i]);
    }
  }
}

/** a:lnSpc>a:spcPct upsert — lnSpc 는 CT_TextParagraphProperties 첫 자식.
 *  모델 lineSpacing 은 %(100=1줄) → spcPct val 은 1000분율(100000=100%). */
function patchLineSpacing(xmlDoc: Document, pPr: Element, para: PptxParagraph): void {
  const ls = para.spacing?.lineSpacing;
  let lnSpc = directChildNS(pPr, A_NS, 'lnSpc');
  if (ls === undefined || ls <= 0) {
    if (lnSpc) pPr.removeChild(lnSpc);
    return;
  }
  if (!lnSpc) {
    lnSpc = xmlDoc.createElementNS(A_NS, 'a:lnSpc');
    pPr.insertBefore(lnSpc, pPr.firstChild);
  }
  while (lnSpc.firstChild) lnSpc.removeChild(lnSpc.firstChild);
  const pct = xmlDoc.createElementNS(A_NS, 'a:spcPct');
  pct.setAttribute('val', String(Math.round(ls * 1000)));
  lnSpc.appendChild(pct);
}

/** 글머리 멤버(buNone|buChar|buAutoNum) 교체 — 배타 선택이라 기존 멤버를 제거한
 *  뒤 스키마 위치(tabLst/defRPr/extLst 앞)에 삽입한다. */
function patchBullet(xmlDoc: Document, pPr: Element, para: PptxParagraph): void {
  for (const name of ['buNone', 'buChar', 'buAutoNum']) {
    const el = directChildNS(pPr, A_NS, name);
    if (el) pPr.removeChild(el);
  }
  let bullet: Element | null = null;
  if (para.bulletType === 'char') {
    bullet = xmlDoc.createElementNS(A_NS, 'a:buChar');
    bullet.setAttribute('char', para.bulletChar || '•');
  } else if (para.bulletType === 'autoNum') {
    bullet = xmlDoc.createElementNS(A_NS, 'a:buAutoNum');
    bullet.setAttribute('type', para.bulletAutoNumType || 'arabicPeriod');
  } else if (para.bulletType === 'none') {
    bullet = xmlDoc.createElementNS(A_NS, 'a:buNone');
  }
  if (!bullet) return;
  pPr.insertBefore(bullet, firstDirectChildOfAny(pPr, ['tabLst', 'defRPr', 'extLst']));
}

function patchRun(xmlDoc: Document, rEl: Element, run: PptxRun): void {
  // 텍스트는 기존 XML 값과 다를 때만 쓴다 — 무변경 dirty 슬라이드에서
  // 의미 중립(no-op) 을 보장하기 위함.
  const tEl = rEl.getElementsByTagNameNS(A_NS, 't')[0];
  if (tEl && run.text !== undefined && tEl.textContent !== run.text) {
    tEl.textContent = run.text;
  }

  // 서식(rPr)은 편집기가 실제로 변경한 런(run.dirty)에만 쓴다.
  // 모델 서식 값은 테마/마스터 상속의 "해석 결과"라, 무조건 되쓰면 원본에
  // 없던 명시 속성이 구워져(schemeClr→srgbClr, 테마폰트→해석명, 상속 sz
  // 명시화) 테마 연결이 끊기는 의미 변경이 된다.
  if (!run.dirty) return;

  let rPr = rEl.getElementsByTagNameNS(A_NS, 'rPr')[0];
  if (!rPr) {
    rPr = xmlDoc.createElementNS(A_NS, 'a:rPr');
    rEl.insertBefore(rPr, rEl.firstChild);
  }

  if (run.formatPainterDirty) {
    patchPaintedRun(xmlDoc, rPr, run, setSolidFillColor);
    return;
  }

  if (run.fontFamiliesDirty) {
    for (const [name, family] of [['latin', run.fontFamily], ['ea', run.eaFontFamily], ['cs', run.csFontFamily]] as const) {
      if (!family) continue;
      let font = directChildNS(rPr, A_NS, name);
      if (!font) {
        font = xmlDoc.createElementNS(A_NS, `a:${name}`);
        const nextNames = name === 'latin' ? ['ea', 'cs', 'sym', 'hlinkClick', 'hlinkMouseOver', 'rtl', 'extLst']
          : name === 'ea' ? ['cs', 'sym', 'hlinkClick', 'hlinkMouseOver', 'rtl', 'extLst']
          : ['sym', 'hlinkClick', 'hlinkMouseOver', 'rtl', 'extLst'];
        const before = Array.from(rPr.children).find(el => el.namespaceURI === A_NS && nextNames.includes(el.localName));
        rPr.insertBefore(font, before ?? null);
      }
      font.setAttribute('typeface', family);
    }
  }

  if (run.bold !== undefined) rPr.setAttribute('b', run.bold ? '1' : '0');
  if (run.italic !== undefined) rPr.setAttribute('i', run.italic ? '1' : '0');
  if (run.underline !== undefined) rPr.setAttribute('u', run.underline ? 'sng' : 'none');
  if (run.strike !== undefined) rPr.setAttribute('strike', run.strike ? 'sngStrike' : 'noStrike');
  if (run.fontSize !== undefined) rPr.setAttribute('sz', String(Math.round(run.fontSize * 100)));

  // 색은 srgbClr@val(6-hex)로만 유효하다. rgba(반투명)/named 를 그대로 val 에
  // 굽던 버그를 setSolidFillColor(검증+a:alpha 보존)로 막는다. 무효색은 건드리지 않음.
  // directChildNS 로 문자-fill solidFill 만 찾는다(재귀면 a:ln 텍스트외곽선의 solidFill
  // 을 오취득해 글자색이 외곽선에 착지한다).
  if (run.color && parseColorHexAlpha(run.color)) {
    let solidFill = directChildNS(rPr, A_NS, 'solidFill');
    if (!solidFill) {
      solidFill = xmlDoc.createElementNS(A_NS, 'a:solidFill');
      rPr.appendChild(solidFill);
    }
    setSolidFillColor(xmlDoc, solidFill, run.color);
  }

  if (run.fontFamily) {
    let latin = rPr.getElementsByTagNameNS(A_NS, 'latin')[0];
    if (!latin) {
      latin = xmlDoc.createElementNS(A_NS, 'a:latin');
      rPr.appendChild(latin);
    }
    latin.setAttribute('typeface', run.fontFamily);
  }
}

/* ------------------------------------------------------------------ */
/* CT_ShapeProperties 순서 유지 삽입                                     */
/* ------------------------------------------------------------------ */

// CT_ShapeProperties 시퀀스: xfrm → geometry → fill → ln → effect → scene3d → sp3d → extLst.
// 새 fill/ln 을 append 하면 뒤따르는 effectLst/scene3d/sp3d/extLst 뒤로 밀려 순서 위반
// (PowerPoint content error/복구) → 시퀀스상 뒤 요소 앞에 삽입한다.
const SPPR_AFTER_FILL = ['ln', 'effectLst', 'effectDag', 'scene3d', 'sp3d', 'extLst'];
const SPPR_AFTER_LN = ['effectLst', 'effectDag', 'scene3d', 'sp3d', 'extLst'];

function insertInSpPrOrder(spPr: Element, el: Element, beforeLocalNames: string[]): void {
  for (let i = 0; i < spPr.childNodes.length; i++) {
    const c = spPr.childNodes[i];
    if (c.nodeType === 1 && (c as Element).namespaceURI === A_NS
        && beforeLocalNames.includes((c as Element).localName)) {
      spPr.insertBefore(el, c);
      return;
    }
  }
  spPr.appendChild(el);
}

/** el 의 직계 자식 중 localNames 에 든 첫 A_NS 요소(문서순). */
function firstDirectChildOfAny(el: Element, localNames: string[]): Element | null {
  for (let i = 0; i < el.childNodes.length; i++) {
    const c = el.childNodes[i];
    if (c.nodeType === 1 && (c as Element).namespaceURI === A_NS && localNames.includes((c as Element).localName)) {
      return c as Element;
    }
  }
  return null;
}

// EG_FillProperties / EG_LineFillProperties — maxOccurs=1 배타 선택. 새 채움을 넣기 전
// 기존 멤버를 반드시 치환해야 한다(안 하면 solidFill+gradFill 등 2개=스키마 위반).
const FILL_MEMBERS = ['noFill', 'solidFill', 'gradFill', 'blipFill', 'pattFill', 'grpFill'];
const LINE_FILL_MEMBERS = ['noFill', 'solidFill', 'gradFill', 'pattFill'];

/* ------------------------------------------------------------------ */
/* Fill 패치                                                            */
/* ------------------------------------------------------------------ */

function patchFill(xmlDoc: Document, spPr: Element, fill: PptxFill): void {
  // 기존 fill 멤버는 solidFill 만이 아니라 gradFill/blipFill/pattFill/noFill 도 될 수 있다.
  // directChildNS('solidFill') 만 보면 그 위에 solidFill 을 덧붙여 fill 멤버가 2개가 된다
  // (배타 선택 위반). 어떤 멤버든 찾아 치환한다. 재귀 대신 직계만(a:ln 외곽선 fill 오취득 방지).
  const existing = firstDirectChildOfAny(spPr, FILL_MEMBERS);

  if (fill.type === 'solid' && parseColorHexAlpha(fill.color)) {
    let solidFill: Element;
    if (existing && existing.localName === 'solidFill') {
      solidFill = existing; // 재사용
    } else {
      solidFill = xmlDoc.createElementNS(A_NS, 'a:solidFill');
      if (existing) {
        existing.parentNode?.insertBefore(solidFill, existing); // 기존 멤버 자리에 치환
        existing.parentNode?.removeChild(existing);
      } else {
        insertInSpPrOrder(spPr, solidFill, SPPR_AFTER_FILL);
      }
    }
    // rgba(반투명)/named 를 val 에 그대로 굽던 버그를 검증+alpha 보존으로 막는다.
    setSolidFillColor(xmlDoc, solidFill, fill.color);
  } else if (fill.type === 'none') {
    if (existing && existing.localName === 'noFill') return; // 이미 noFill
    if (existing) {
      const noFill = xmlDoc.createElementNS(A_NS, 'a:noFill');
      existing.parentNode?.insertBefore(noFill, existing);
      existing.parentNode?.removeChild(existing);
    } else {
      insertInSpPrOrder(spPr, xmlDoc.createElementNS(A_NS, 'a:noFill'), SPPR_AFTER_FILL);
    }
  }
}

/* ------------------------------------------------------------------ */
/* Outline 패치                                                         */
/* ------------------------------------------------------------------ */

function patchOutline(xmlDoc: Document, spPr: Element, outline: PptxOutline): void {
  let lnEl = directChildNS(spPr, A_NS, 'ln');
  if (!lnEl) {
    lnEl = xmlDoc.createElementNS(A_NS, 'a:ln');
    insertInSpPrOrder(spPr, lnEl, SPPR_AFTER_LN);
  }

  if (outline.width !== undefined) {
    lnEl.setAttribute('w', String(Math.round(outline.width * 12700)));
  }

  if (outline.color && parseColorHexAlpha(outline.color)) {
    // a:ln 의 fill 도 배타 선택 — 기존 noFill/gradFill/pattFill 을 치환해야 한다
    // (안 하면 ln 안에 solidFill+noFill 등 2개=CT_LineProperties 위반).
    const existing = firstDirectChildOfAny(lnEl, LINE_FILL_MEMBERS);
    let solidFill: Element;
    if (existing && existing.localName === 'solidFill') {
      solidFill = existing;
    } else {
      solidFill = xmlDoc.createElementNS(A_NS, 'a:solidFill');
      if (existing) {
        lnEl.insertBefore(solidFill, existing); // 기존 멤버 자리에 치환
        lnEl.removeChild(existing);
      } else {
        // fill 은 a:ln 의 첫 자식군(prstDash/join/헤드·테일 앞).
        lnEl.insertBefore(solidFill, lnEl.firstChild);
      }
    }
    // rgba/named 를 val 에 그대로 굽던 버그를 검증+alpha 보존으로 막는다.
    setSolidFillColor(xmlDoc, solidFill, outline.color);
  }
}
